// THE ANALYSIS SHEET (CR-20260929-001). Pure TypeScript, no Angular, no market data: every rule of
// system-description.md §4–§5 that can be checked with numbers lives here, so it can be unit-tested
// without a browser. The panel owns ONE PhaSheet for its lifetime and throws it away on close.
//
// Vocabulary (system-description.md §9): a row is an UNDERLYING (stock/ETF, long only in v1); its
// ORIGIN is `held` (preloaded from a portfolio, M5) or `new` (added here); a held row that is removed
// is CROSSED OUT, never deleted; the two LOCKS freeze shares or % through a balance move (M2).

import {duplicateContracts, UniqueOptionEdit} from './pha-contract-validation';
import {optionContributionKey} from './session/pha-order-identity';
import {PhaRoundingInput, PhaRoundingReport} from './pha-rounding-optimizer';

import {PhaOptionRow, PhaOptionSection, PhaOptionStrategyType, PhaStrategyLeg, strategyRatio} from './pha-option-strategy';

export type PhaRowOrigin = 'new' | 'held';

/**
 * The SIDE of every row (owner, 2026-09-29 vrequest, item 4): the direction of the position, BUY or
 * SELL, editable on the row. On an option row it sets the SIGN of the row's dollars (SELL = a credit,
 * BUY = a debit); on an Underlying row it is a label only — v1 has no short-underlying arithmetic.
 */
export type PhaSide = 'Buy' | 'Sell';
export const PHA_SIDES: PhaSide[] = ['Buy', 'Sell'];

/** The TYPE of the asset on a row (item 4): STOCK on an Underlying row, CALL or PUT on an option row. */
export type PhaUnderlyingAssetType = 'Stock' | 'Future';
export type PhaAssetType = PhaUnderlyingAssetType | 'Call' | 'Put';
export type PhaOptionType = 'Call' | 'Put';

export interface PhaUnderlyingRow {
   id: number;
   ticker: string;
   displayName: string;
   origin: PhaRowOrigin;
   /** BUY (long) by default; SELL is a label only in v1 (see PhaSide). */
   side: PhaSide;
   /** Stock/ETF or a long call used as an underlying. */
   assetType: PhaAssetType;
   /** Call rows: ticker is the full contract code; shares stores the number of contracts. */
   callUnderlying?: string;
   callStrike?: number;
   callExpirationDate?: string;
   callExpirationTicker?: string;
   /** Owner placeholder: call delta is 1 until a delta source is connected. */
   callDelta?: number;
   /** Held rows only: the portfolio position this row came from (M5). */
   sourcePositionId?: string;
   crossedOut: boolean;
   selected: boolean;

   shares: number;
   /** Last equity price, or null while unknown. */
   price: number | null;
   priceStale: boolean;
   /** Beta from the external feed, or null while unknown / unavailable (M2). */
   beta: number | null;

   /** A typed % of portfolio: a REQUEST that stands until a balance move or a manual fix (§5). */
   pctOverride: number | null;

   sharesLock: boolean;
   pctLock: boolean;

   // ---- derived (recomputed as one unit; never set by hand) ----
   dollars: number | null;
   pct: number | null;
   betaDelta: number | null;
}

/**
 * A PREMIUM row (system-description.md section 4 "The Premium section"): what CAN be sold against a
 * held name — in v1 always a call. The row follows its Underlying row (shares held, contracts
 * available, removal, cross-out). Expiration and strike are SELECTED from the listed chain, never
 * typed; the option's price is the quote's mid; dollars are a credit, shown positive.
 */
export interface PhaOptionAmounts {
   /** Signed price per strategy unit on its anchor only; derived, never saved. Null = unavailable. */
   netStrategyPrice: number | null;
   dollarsExisting?: number | null;
   dollarsTransaction?: number | null;
   hasHolding?: boolean;
   hasTransaction?: boolean;
   protectedExisting?: number | null;
   protectedTransaction?: number | null;
}

/** Signed book quantities, keyed by section + '|' + contract; supplied from the session. */
export interface PhaOptionHoldings { [key: string]: number; }

export interface PhaPremiumRow extends PhaOptionAmounts, PhaStrategyLeg {
   /** A restored overallocated draft remains intact until the owner repairs its backing. */
   preserveInvalidBacking?: boolean;
   id: number;
   /** SELL by default (premium is collected); BUY makes the row a debit (item 4). */
   side: PhaSide;
   /** CALL by default; a PUT when loaded from a portfolio's Premium bucket (item 10 — mapped by bucket name only). */
   optionType: PhaOptionType;
   /** The held Underlying row this row follows; NULL for an index row (owner, 2026-09-29: the Premium list offers the index options too). */
   underlyingRowId: number | null;
   /**
    * True = follows a held name: its contracts come out of that underlying's pool (R10). False = an
    * index row: no shares back it, the contracts are typed freely from 0 ([my call], 2026-09-29 —
    * the description's "index call, or a leg of an index vertical" has no pool to draw from).
    */
   pooled: boolean;
   ticker: string;
   displayName: string;
   /** `held` = loaded from the portfolio (item 10): removed = crossed out, restorable; `new` = deleted. */
   origin: PhaRowOrigin;
   /** The row's OWN cross-out (a held row removed); `crossedOut` also follows the underlying's. */
   removed: boolean;
   crossedOut: boolean;

   sharesHeld: number;
   /**
    * What THIS row may still sell: floor(shares / 100) minus what the underlying's OTHER Premium
    * rows already sell (owner ruling 2026-09-29: one pool per underlying, never per row) — "so we
    * are never naked".
    */
   contractsAvailable: number;
   /** 0 .. max(contractsAvailable, heldContracts). */
   contractsSelling: number;
   /**
    * What the BOOK holds on this contract (a held row, at import); 0 for a new row. The pool never
    * cuts a row below it: the book is the truth, and an over-covered holding must load as it is —
    * otherwise the import itself manufactures a change (owner smoke 2026-09-30: an acquired 2-lot
    * came back as 1 with a phantom buy-back, because the pool of 203 shares held only 2 contracts
    * for two rows).
    */
   heldContracts: number;

   /** The listed expirations for the ticker (loaded by the panel); empty until the chain arrives. */
   expirations: PhaExpiration[];
   expiration: PhaExpiration | null;
   strike: number | null;
   /** How far above spot the selected strike sits, in % of spot. */
   pctOtm: number | null;
   /** True = the header's OTM mover skips this row (R18); the row's own % OTM box still works. */
   otmLock: boolean;

   /** The option contract's quote ticker, once expiration + strike are selected. */
   optionTicker: string | null;
   bid: number | null;
   ask: number | null;
   /** The feed's own mid and last, the platform's fallbacks when a side is missing. */
   feedMid: number | null;
   feedLast: number | null;
   /** (bid + ask) / 2 when two-sided, else the feed's mid, else its last (the platform's rule); null = unpriced. */
   price: number | null;
   /** price x 100 x contractsSelling, signed by the side: SELL = a credit (+), BUY = a debit (-). Null while unpriced. */
   dollars: number | null;
}

/** The slice of an option-chain expiration the sheet needs (the panel maps the wire descriptor). */
export interface PhaExpiration {
   /** "yyyy-MM-dd" */
   date: string;
   /** What the trader reads, e.g. "17-Oct-26 (18d)". */
   label: string;
   strikes: number[];
   /** The wire descriptor, handed back to the panel to build the option ticker. */
   descriptor: any;
}

/**
 * A PROTECTION row (system-description.md section 4 "The Protection section"): what is bought to
 * protect the position — in v1 always a put, on a market proxy from the protection list (SPX, XSP,
 * SPY — ruling R2) or on a held name. Two rows make a put spread; several instruments may coexist.
 * Dollars are a DEBIT, shown negative; "dollars protected" = strike x 100 x contracts.
 */
export interface PhaProtectionRow extends PhaOptionAmounts, PhaStrategyLeg {
   id: number;
   /** BUY by default (protection is bought); SELL makes the row a credit (item 4). */
   side: PhaSide;
   /** PUT by default; a CALL when loaded from a portfolio's Protection bucket (item 10 — mapped by bucket name only). */
   optionType: PhaOptionType;
   symbol: string;
   displayName: string;
   /** True when the symbol was a held name when the row was made: the row then follows that underlying. */
   onHeldName: boolean;
   /** `held` = loaded from the portfolio (item 10): removed = crossed out, restorable; `new` = deleted. */
   origin: PhaRowOrigin;
   /** The row's OWN cross-out (a held row removed). */
   removed: boolean;
   /** The row's own cross-out, or the held name's (the row follows it). */
   crossedOut: boolean;

   expirations: PhaExpiration[];
   expiration: PhaExpiration | null;
   strike: number | null;
   /** How far BELOW spot the selected strike sits, in % of spot (positive number). */
   pctOtm: number | null;
   /** True = the header's OTM mover skips this row (R18); the row's own % OTM box still works. */
   otmLock: boolean;
   contracts: number;

   optionTicker: string | null;
   bid: number | null;
   ask: number | null;
   feedMid: number | null;
   feedLast: number | null;
   /** (bid + ask) / 2 when two-sided, else the feed's mid, else its last (the platform's rule); null = unpriced. */
   price: number | null;
   /** price x 100 x contracts, signed by the side: BUY = a debit (-), SELL = a credit (+). Null while unpriced. */
   dollars: number | null;
   /** strike x 100 x contracts: the coverage, to compare with total beta-delta. */
   dollarsProtected: number | null;
}

export interface PhaProtectionTotals {
   mixed?: boolean;
   hasHoldings?: boolean;
   hasTransactions?: boolean;
   protectedExisting?: number;
   protectedTransaction?: number;
   /** Sum of the debits (negative). */
   dollars: number;
   /** The Hedge Pad split (owner, 2026-10-01 vrequest): see PhaPremiumTotals. */
   dollarsExisting: number;
   dollarsTransaction: number;
   dollarsProtected: number;
   unpricedRows: number;
}



/** New Premium options start 10% OTM; Protection starts ATM (owner, 2026-10-07). */
export const PHA_DEFAULT_PCT_OTM = 10;
export const PHA_DEFAULT_PROTECTION_PCT_OTM = 0;
/** A row's % OTM range (owner, 2026-09-29): -100 lets the option move to the other side of spot. */
export const PHA_MIN_PCT_OTM = -100;
export const PHA_MAX_PCT_OTM = 100;

/** The protection list (ruling R2): a curated constant, SPX / XSP / SPY. Held names are offered beside it. */
export const PHA_PROTECTION_LIST = ['SPX', 'XSP', 'SPY'];

/** One entry of a Premium row's name selector: a held Underlying row, or an index option (owner, 2026-09-29). */
export interface PhaPremiumChoice {
   /** The selector's value: "u:<underlying row id>" for a held name, "i:<ticker>" for an index option. */
   key: string;
   underlyingRowId: number | null;
   ticker: string;
   displayName: string;
}

export function premiumChoiceKeyFor(underlyingRowId: number | null, ticker: string): string {
   return underlyingRowId !== null && underlyingRowId !== undefined ? 'u:' + underlyingRowId : 'i:' + ticker;
}

/** The choice key a Premium row currently sits on (what its selector shows). */
export function premiumChoiceKeyOf(p: PhaPremiumRow): string {
   return premiumChoiceKeyFor(p.pooled ? p.underlyingRowId : null, p.ticker);
}

export interface PhaPremiumTotals {
   mixed?: boolean;
   hasHoldings?: boolean;
   hasTransactions?: boolean;
   /** Single-mode amount; use the two explicit totals in mixed mode. */
   dollars: number;
   /** Remaining holding valuation and proposed trade cash flow are separate, never added together. */
   dollarsExisting: number;
   dollarsTransaction: number;
   /** Rows selected (expiration + strike) but without a two-sided quote: excluded from the total. */
   unpricedRows: number;
}



/** The Net Hedge section: premium collected (credit), protection cost (debit), and the net. */
export interface PhaNetHedge {
   mixed?: boolean;
   premiumMixed?: boolean;
   protectionMixed?: boolean;
   premiumExisting?: number;
   premiumTransaction?: number;
   protectionExisting?: number;
   protectionTransaction?: number;
   netExisting?: number;
   netTransaction?: number;
   premium: number;
   protection: number;
   net: number;
}

/** One held stock/ETF position handed over from a portfolio (system-description.md section 5 "Opening"). */
export interface PhaHeldPosition {
   ticker: string;
   displayName: string;
   shares: number;
   positionId: string;
   assetType?: PhaUnderlyingAssetType;
}

/**
 * One held OPTION position handed over from a portfolio's Premium or Protection bucket (owner,
 * 2026-09-29 vrequest, item 10): the section comes from the BUCKET NAME only (owner ruling), the
 * contract from the position's option ticker, the side from the sign of the net position.
 */
export interface PhaHeldOption {
   optionTicker?: string;
   section: 'premium' | 'protection';
   /** The option's underlying ticker (AAPL, SPX, ...). */
   underlying: string;
   displayName: string;
   optionType: PhaOptionType;
   strike: number;
   /** "yyyy-MM-dd", as the option ticker carries it. */
   expirationDate: string;
   /** The option root of the ticker (SPX vs SPXW), to pick the right listed expiration on the same date. */
   expirationTicker: string;
   contracts: number;
   side: PhaSide;
   positionId: string;
}

/**
 * The COMBO an analysis was opened from (ruling R11: the combo is the unit of analysis, a portfolio
 * holds several): read-only context, shown on screen, never persisted.
 */
export interface PhaPortfolioContext {
   comboId: string;
   comboName: string;
   portfolioId: string;
   portfolioName: string;
   ownerUserName: string;
}

export interface PhaTotals {
   dollars: number;
   pct: number;
   /** Whole market-equivalent shares: the rounded sum of the rows' whole β-Δ (item 7). */
   betaDelta: number | null;
   /** Account value minus stock market value: the combo's current free cash. */
   freeCash: number | null;
   /** False when the portfolio exceeds its configured maximum stock allocation. */
   pctConsistent: boolean;
   /** False when stock market value exceeds the allowed portion of account value. */
   dollarsConsistent: boolean;
}

const PCT_TOLERANCE = 0.005;

/**
 * The market itself (owner ruling 2026-09-29): a broad S&P 500 proxy has beta 1 BY DEFINITION, so
 * these never wait on the provider (which publishes no beta for an ETF or an index anyway).
 */
export const PHA_MARKET_PROXIES = ['SPY', 'SPX', 'XSP', 'VOO', 'IVV', 'SPLG', 'ES', 'MES'];

export function isMarketProxy(ticker: string): boolean {
   return !!ticker && PHA_MARKET_PROXIES.indexOf(ticker.toUpperCase()) >= 0;
}

export type PhaBalanceMode = 'percent' | 'beta' | 'rounding';

export interface PhaOptimizationSnapshot {
   rows: Array<{id: number, shares: number, pctOverride: number | null}>;
   premium: Array<{id: number, contractsSelling: number, preserveInvalidBacking?: boolean}>;
   protection: Array<{id: number, contracts: number}>;
   lastBalance: PhaBalanceMode | null;
}

/** What a balance move did. `applied` false = NOTHING was rewritten (all-or-nothing, section 5/6). */
export interface PhaBalanceResult {
   applied: boolean;
   /** Plain-language reason for a refusal; names the row when one row is the cause. */
   reason?: string;
   rowTicker?: string;
}

/** Accounting reference marks survive saved designs; they never replace incoming market quotes. */
export interface PhaAccountPosition { ticker: string; quantity: number; unitPrice: number; pointValue?: number; needsPointValueRebase?: boolean; }
export interface PhaAccountValuation { cash: number | null; positions: PhaAccountPosition[]; }

export class PhaSheet {
   constructor(private readonly optionExpired: (ticker: string) => boolean = () => false,
               private readonly resolveDisplayName: (ticker: string) => string = () => null,
               private readonly resolveUnderlyingType: (ticker: string) => PhaUnderlyingAssetType = () => null,
               private readonly resolvePointValue: (ticker: string) => number = () => null) {}

   displayNameForSymbol(symbol: string): string {
      return this.resolveDisplayName(symbol)
         || [...this.premiumRows, ...this.protectionRows].find(r => ('ticker' in r ? r.ticker : r.symbol) === symbol)?.displayName
         || this.rows.find(r => r.assetType !== 'Call' && r.ticker === symbol)?.displayName || symbol;
   }

   contractValidationEnabled = true;
   contractConflict: string | null = null;
   private checkingOptionEdit = false;
   private editingStrategyLifecycle = false;

   validateOptionEdit<T>(name: string, args: any[], execute: (target: PhaSheet) => T): T {
      if (!this.contractValidationEnabled || this.checkingOptionEdit) { return execute(this); }
      const prior = new Set(duplicateContracts(this).map(c => c.key + ':' + c.rowIds.slice().sort().join(',')));
      const trial = Object.create(Object.getPrototypeOf(this)) as PhaSheet;
      Object.keys(this).forEach(key => {
         const value = this[key];
         trial[key] = Array.isArray(value) ? value.map(item => item && typeof item === 'object' ? {...item} : item)
            : value && typeof value === 'object' ? {...value} : value;
      });
      trial.checkingOptionEdit = true;
      execute(trial);
      const priorBacking = this.twoForOneBackingProblems();
      const backing = trial.twoForOneBackingProblems().find(p => !priorBacking.some(old => old.id === p.id && old.deficit >= p.deficit));
      if (backing) {
         this.contractConflict = 'Two-for-One Requires 100 Unallocated Stock Shares per Unit. Close or Revoke the Protected Position First.';
         return this.rejectedEdit(name, args);
      }
      const collision = duplicateContracts(trial).find(c => !prior.has(c.key + ':' + c.rowIds.slice().sort().join(',')));
      if (collision) {
         this.contractConflict = 'Contract Already Used: ' + collision.description + '. Choose Distinct Contracts.';
         if (name === 'setStrategyExpiration' || name === 'moveStrategy' || name === 'setStrategyGap'
            || name.indexOf('shift') === 0 || name === 'repointOptionStrategy') { return [] as any; }
         const id = typeof args[0] === 'number' ? args[0] : args[1];
         return (this.premiumRows.find(r => r.id === id) || this.protectionRows.find(r => r.id === id) || null) as any;
      }
      this.checkingOptionEdit = true;
      try { return execute(this); } finally { this.checkingOptionEdit = false; }
   }

   private rejectedEdit(name: string, args: any[]): any {
      if (name === 'balance') { return {applied: false, reason: this.contractConflict}; }
      if (name.indexOf('remove') === 0) { return 'none'; }
      if (['moveStrategy', 'setStrategyGap', 'setStrategyExpiration', 'repointOptionStrategy'].indexOf(name) >= 0
         || name.indexOf('shift') === 0) { return []; }
      if (name === 'addTwoForOne') { return null; }
      const id = typeof args[0] === 'number' ? args[0] : args[1];
      return this.premiumRows.find(r => r.id === id) || this.protectionRows.find(r => r.id === id) || null;
   }

   /** Pool usage is per trade, not per option leg. Frozen and held commitments take priority. */
   private premiumUnits(rows: PhaPremiumRow[]): number {
      const seen = new Set<string>();
      return rows.reduce((sum, row) => {
         if (row.strategyId && seen.has(row.strategyId)) { return sum; }
         if (row.strategyId) { seen.add(row.strategyId); }
         const group = row.strategyId ? rows.filter(r => r.strategyId === row.strategyId) : [row];
         return sum + Math.max(...group.map(r => r.contractsSelling / strategyRatio(r)));
      }, 0);
   }

   freeStockUnits(underlyingId: number, excludingStrategy?: string): number {
      const u = this.rows.find(r => r.id === underlyingId && r.assetType === 'Stock' && !r.crossedOut && r.side === 'Buy');
      if (!u) { return 0; }
      const used = this.premiumUnits(this.premiumRows.filter(r => r.underlyingRowId === underlyingId
         && !r.crossedOut && !r.removed && !this.isExpired(r) && (!excludingStrategy || r.strategyId !== excludingStrategy)));
      return Math.max(0, Math.floor(u.shares / 100) - used);
   }

   @UniqueOptionEdit
   addTwoForOne(): PhaPremiumRow | null {
      const u = this.rows.find(r => this.freeStockUnits(r.id) >= 1);
      if (!u) { this.contractConflict = 'You Have Not Enough Stocks For This Strategy'; return null; }
      const row = this.addPremiumRow(u.id, 'Call', 'Buy');
      this.linkOptionStrategy('premium', row.id, 'Two-for-One', []);
      return row;
   }

   /** Preserve held/locked commitments even when a saved design is inconsistent. */
   private twoForOneBackingProblems(): Array<{id: number, deficit: number}> {
      const ids = Array.from(new Set(this.premiumRows.filter(r => r.strategyType === 'Two-for-One')
         .map(r => r.underlyingRowId)));
      return ids.map(id => {
         const u = this.rows.find(r => r.id === id && r.assetType === 'Stock' && !r.crossedOut && r.side === 'Buy');
         const rows = this.premiumRows.filter(r => r.underlyingRowId === id && !this.isExpired(r));
         const committed = rows.filter(r => this.isSessionLocked(r.id) || r.heldContracts > 0);
         const required = this.premiumUnits(committed.map(r => ({...r,
            contractsSelling: Math.max(r.heldContracts, this.isSessionLocked(r.id) ? r.contractsSelling : 0)})));
         return {id, deficit: required - (u ? Math.floor(u.shares / 100) : 0)};
      }).filter(p => p.deficit > 0);
   }

   /** Used again at export; restored data cannot bypass the owning model's rules. */
   twoForOneProblem(row: PhaPremiumRow): string | null {
      if (row.strategyType !== 'Two-for-One') { return null; }
      const group = this.strategyRows('premium', row.id) as PhaPremiumRow[];
      const a = group[0], b = group[1];
      if (!row.strategyId || group.length !== 2 || !a || !b || a.strategyLeg !== 0 || b.strategyLeg !== 1
         || group.some(r => r.strategyType !== 'Two-for-One' || r.optionType !== 'Call' || r.ticker !== a.ticker
            || r.underlyingRowId !== a.underlyingRowId || !r.pooled)
         || a.side !== 'Buy' || b.side !== 'Sell' || !Number.isInteger(a.contractsSelling) || a.contractsSelling < 0
         || b.contractsSelling !== 2 * a.contractsSelling) { return 'Invalid Two-for-One Structure or Quantity Ratio.'; }
      const u = this.rows.find(r => r.id === a.underlyingRowId && r.assetType === 'Stock' && r.side === 'Buy' && !r.crossedOut);
      if (!u || this.premiumUnits(this.premiumRows.filter(r => r.underlyingRowId === u.id && !r.crossedOut && !this.isExpired(r))) > Math.floor(u.shares / 100)
         || this.twoForOneBackingProblems().some(p => p.id === u.id)) { return 'Insufficient Stock Backing for Two-for-One.'; }
      if (!a.expiration || !b.expiration || a.expiration.date !== b.expiration.date
         || (a.expiration.descriptor && a.expiration.descriptor.expirationTicker) !== (b.expiration.descriptor && b.expiration.descriptor.expirationTicker)
         || a.strike === null || b.strike === null || b.strike <= a.strike || !isFinite(b.strategyGap) || b.strategyGap <= 0
         || Math.abs(b.strike - a.strike - b.strategyGap) > 1e-8 || !a.optionTicker || !b.optionTicker) {
         return 'Complete Both Two-for-One Calls with a Short Strike Above the Long Strike.';
      }
      return null;
   }

   twoForOneReduction(row: PhaPremiumRow): boolean {
      const group = this.strategyRows('premium', row.id) as PhaPremiumRow[];
      return row.strategyType === 'Two-for-One' && group.length > 0 && group.every(r => r.origin === 'held' && r.contractsSelling <= r.heldContracts)
         && group.some(r => r.contractsSelling < r.heldContracts)
         && (group.every(r => r.contractsSelling === 0) || group.length === 2 && group[0].side === 'Buy'
            && group[1].side === 'Sell' && group[1].contractsSelling === 2 * group[0].contractsSelling);
   }

   preserveRestoredTwoForOne(): void {
      this.premiumRows.filter(r => r.strategyType === 'Two-for-One').forEach(r => {
         const u = this.rows.find(stock => stock.id === r.underlyingRowId && stock.assetType === 'Stock' && !stock.crossedOut && stock.side === 'Buy');
         r.preserveInvalidBacking = !u || this.premiumUnits(this.premiumRows.filter(p => p.underlyingRowId === r.underlyingRowId
            && !p.crossedOut && !p.removed && !this.isExpired(p))) > Math.floor(u.shares / 100);
      });
   }

   /** Creation resolves only the anchor at the supplied offset; strategy wings await the user's distances. */
   @UniqueOptionEdit
   initializeOptionExpiration(section: PhaOptionSection, id: number, expiration: PhaExpiration,
                              creationPctOtm: number = PHA_DEFAULT_PCT_OTM): void {
      const group = this.strategyRows(section, id);
      const before = new Set(duplicateContracts(this).map(c => c.key + ':' + c.rowIds.slice().sort().join(',')));
      if (group[0] && group[0].strategyId) {
         const anchor = group[0];
         const spot = section === 'premium' ? this.spotOf((anchor as PhaPremiumRow).ticker)
            : this.spotOfSymbol((anchor as PhaProtectionRow).symbol);
         group.forEach(r => { r.expiration = expiration; r.strategyGap = undefined; r.strategyUseAdjacent = false; });
         this.applyStrategyStrikes(group, [resolveStrikeByType(anchor.optionType, expiration, spot,
            section === 'protection' || anchor.strategyType === 'Two-for-One' ? 0 : clampPctOtm(creationPctOtm))]);
      } else if (section === 'premium') { this.setPremiumExpiration(id, expiration, creationPctOtm); }
      else { this.setProtectionExpiration(id, expiration); }
      const newCollision = () => duplicateContracts(this).find(c => !before.has(c.key + ':' + c.rowIds.slice().sort().join(',')));
      const collision = newCollision();
      if (collision) {
         if (group[0] && group[0].strategyType === 'Two-for-One') {
            this.applyStrategyStrikes(group, []);
            this.contractConflict = 'The ATM Contract Is Already Used. Choose a Different Contract.';
            return;
         }
         if (group.length) {
            const row = group[0];
            const chain = expiration.strikes.filter(s => isFinite(s)).filter((s, i, all) => all.indexOf(s) === i)
               .sort((a, b) => row.optionType === 'Call' ? a - b : b - a);
            const start = chain.indexOf(row.strike);
            for (const strike of start < 0 ? [] : chain.slice(start + 1)) {
               this.applyStrategyStrikes(group, [strike]);
               // Singles use their selected strike as identity, without a remembered strategy anchor.
               if (!row.strategyId) { row.expectedStrike = undefined; }
               if (!newCollision()) { this.contractConflict = null; return; }
            }
         }
         group.forEach(r => {
            r.expiration = expiration; r.strike = null; r.expectedStrike = undefined;
            if ('contractsSelling' in r) { this.clearPremiumOption(r); } else { this.clearProtectionOption(r); }
         });
         this.contractConflict = 'No Unused Adjacent Strike Is Available: ' + collision.description + '. Choose a Different Contract.';
         this.recompute();
      }
   }

   @UniqueOptionEdit
   repointOptionStrategy(section: PhaOptionSection, id: number, key: string, displayName?: string): PhaOptionRow[] {
      const group = this.strategyRows(section, id);
      if (group.some(r => r.origin === 'held' || r.crossedOut || this.isRowDisabled(r.id))) { return []; }
      if (group[0] && group[0].strategyType === 'Two-for-One') {
         const choice = this.premiumChoices().find(c => c.key === key);
         if (!choice || choice.underlyingRowId === null
            || this.freeStockUnits(choice.underlyingRowId, group[0].strategyId) < (group[0] as PhaPremiumRow).contractsSelling) {
            this.contractConflict = 'Two-for-One Requires Sufficient Unallocated Stock Backing.'; return [];
         }
         const u = this.rows.find(r => r.id === choice.underlyingRowId);
         group.forEach(r => {
            this.repointPremium(r.id, u.id, underlyingSymbol(u), u.displayName, u.crossedOut, backingShares(u), false);
            r.expectedStrike = undefined; r.strategyGap = undefined; r.strategyUseAdjacent = false;
         });
         this.recompute();
         return group;
      }
      group.forEach(r => {
         if (section === 'premium') { this.setPremiumChoice(r.id, key); }
         else { this.setProtectionSymbol(r.id, key, displayName); }
         r.expectedStrike = undefined;
         if (r.strategyId) { r.strategyUseAdjacent = true; }
      });
      return group;
   }

   /** Initial spacing follows actual neighbors in this expiration, never a guessed point step. */
   private adjacentStrategyStrikes(group: PhaOptionRow[], expiration: PhaExpiration, anchor: number, selectedLeg = 0, initialDefault = false): number[] {
      const chain = expiration.strikes.filter(s => isFinite(s)).filter((s, i, all) => all.indexOf(s) === i).sort((a, b) => a - b);
      const direction = group[0].optionType === 'Call' ? 1 : -1;
      const anchorIndex = chain.indexOf(anchor);
      let start = anchorIndex - direction * selectedLeg;
      if (initialDefault && chain.length >= group.length) {
         start = direction > 0 ? Math.min(start, chain.length - group.length) : Math.max(start, group.length - 1);
      }
      const values = group.map((_, i) => anchorIndex < 0 ? null : chain[start + direction * i] === undefined ? null : chain[start + direction * i]);
      group.forEach((r, i) => {
         r.strategyGap = i && values[i] !== null && values[i - 1] !== null ? Math.abs(values[i] - values[i - 1]) : undefined;
         r.strategyUseAdjacent = values.some(value => value === null);
      });
      return values;
   }

   strategyRows(section: PhaOptionSection, id: number): PhaOptionRow[] {
      const rows: PhaOptionRow[] = section === 'premium' ? this.premiumRows : this.protectionRows;
      const row = rows.find(r => r.id === id);
      return !row ? [] : row.strategyId ? rows.filter(r => r.strategyId === row.strategyId)
         .sort((a, b) => a.strategyLeg - b.strategyLeg) : [row];
   }

   /** Insert complete templates; the panel resolves one expiration for all their legs. */
   linkOptionStrategy(section: PhaOptionSection, id: number, type: PhaOptionStrategyType, gaps: number[]): PhaOptionRow[] {
      const anchor = this.strategyRows(section, id)[0];
      if (!anchor || type === 'Single Option') { return anchor ? [anchor] : []; }
      if (type === 'Two-for-One') {
         const p = anchor as PhaPremiumRow;
         if (section !== 'premium' || anchor.strategyId || anchor.origin === 'held' || this.freeStockUnits(p.underlyingRowId) < 1) {
            this.contractConflict = 'You Have Not Enough Stocks For This Strategy'; return [];
         }
         anchor.optionType = 'Call'; anchor.side = 'Buy';
      }
      const groupId = 'pha-' + Date.now() + '-' + anchor.id + '-' + Math.random().toString(36).slice(2);
      const count = type === 'Butterfly' ? 3 : 2;
      const group: PhaOptionRow[] = [anchor];
      for (let i = 1; i < count; i++) {
         // Premium vertical starts Sell/Buy; butterfly uses the pad's Buy/Sell*2/Buy template.
         const side: PhaSide = type === 'Butterfly' ? (i === 1 ? 'Sell' : 'Buy')
            : anchor.side === 'Sell' ? 'Buy' : 'Sell';
         const next = section === 'premium'
            ? ('underlyingRowId' in anchor && anchor.pooled ? this.addPremiumRow(anchor.underlyingRowId, anchor.optionType, side)
               : this.addIndexPremiumRow((anchor as PhaPremiumRow).ticker, anchor.optionType, side))
            : this.addProtectionRow((anchor as PhaProtectionRow).symbol, anchor.displayName, anchor.optionType, side);
         group.push(next);
      }
      if (type === 'Butterfly' || type === 'Two-for-One') { anchor.side = 'Buy'; }
      group.forEach((row, i) => {
         row.strategyId = groupId; row.strategyFamilyId = groupId; row.strategyType = type; row.strategyLeg = i;
         row.strategyGap = i ? gaps[i - 1] : undefined;
         row.strategyUseAdjacent = type !== 'Two-for-One' && gaps.length === 0;
         if ('contractsSelling' in row) { row.contractsSelling = strategyRatio(row); }
         else { row.contracts = strategyRatio(row); }
      });
      this.recompute();
      return group;
   }

   private editableStrategy(section: PhaOptionSection, id: number): PhaOptionRow[] {
      const group = this.strategyRows(section, id);
      return group.some(r => r.crossedOut || r.removed || this.isRowDisabled(r.id)) ? [] : group;
   }

   /** Changing an acquired strategy closes the old group and opens a complete replacement. */
   private replaceHeldStrategy(section: PhaOptionSection, group: PhaOptionRow[]): PhaOptionRow[] {
      if (!group.some(r => r.origin === 'held')) { return group; }
      const groupId = group[0].strategyId + '-replacement-' + (this._seq + 1);
      return group.map(r => {
         const next = r.origin !== 'held' ? r : section === 'premium'
            ? this.splitHeldPremiumRow(r as PhaPremiumRow) : this.splitHeldProtectionRow(r as PhaProtectionRow);
         next.strategyId = groupId;
         return next;
      });
   }

   private applyStrategyStrikes(group: PhaOptionRow[], strikes: number[]): void {
      group.forEach((row, i) => {
         row.expectedStrike = strikes[i] == null || !isFinite(strikes[i]) ? undefined : strikes[i];
         if ('contractsSelling' in row) { this.clearPremiumOption(row); }
         else { this.clearProtectionOption(row); }
         row.strike = row.expiration && row.expiration.strikes.indexOf(strikes[i]) >= 0 ? strikes[i] : null;
      });
      this.recompute();
   }

   @UniqueOptionEdit
   setStrategyExpiration(section: PhaOptionSection, id: number, expiration: PhaExpiration | null): PhaOptionRow[] {
      let group = this.editableStrategy(section, id);
      if (!group.length) { return []; }
      if (group.every(r => r.expiration === expiration)) { return group; }
      const anchor = group[0];
      const spot = section === 'premium' ? this.spotOf((anchor as PhaPremiumRow).ticker)
         : this.spotOfSymbol((anchor as PhaProtectionRow).symbol);
      let strike = anchor.expectedStrike === undefined ? anchor.strike : anchor.expectedStrike;
      const initialDefault = strike === null || strike === undefined;
      if (strike === null || strike === undefined) {
         strike = expiration ? resolveStrikeByType(anchor.optionType, expiration, spot,
            section === 'protection' || anchor.strategyType === 'Two-for-One' ? 0 : PHA_DEFAULT_PCT_OTM) : null;
      }
      group = this.replaceHeldStrategy(section, group);
      group.forEach(r => r.expiration = expiration);
      if (strike !== null && strike !== undefined) {
         const direction = anchor.optionType === 'Call' ? 1 : -1;
         const values = group.some(r => r.strategyUseAdjacent) ? this.adjacentStrategyStrikes(group, expiration, strike, 0, initialDefault) : [strike];
         if (!group.some(r => r.strategyUseAdjacent) && values.length === 1) {
            group.slice(1).forEach(r => values.push(values[values.length - 1] + direction * r.strategyGap));
         }
         this.applyStrategyStrikes(group, values);
      } else {
         group.forEach(r => { if ('contractsSelling' in r) { this.clearPremiumOption(r); } else { this.clearProtectionOption(r); } });
         this.recompute();
      }
      return group;
   }

   @UniqueOptionEdit
   moveStrategy(section: PhaOptionSection, id: number, strike: number): PhaOptionRow[] {
      let group = this.editableStrategy(section, id);
      const selected = group.find(r => r.id === id);
      if (!selected || !isFinite(strike)) { return []; }
      if (selected.strategyType === 'Two-for-One' && selected.strategyLeg === 1) {
         const anchorStrike = group[0].expectedStrike === undefined ? group[0].strike : group[0].expectedStrike;
         if (anchorStrike == null || strike <= anchorStrike) {
            this.contractConflict = 'The Two-for-One Short Strike Must Be Above the Long Strike.'; return [];
         }
         return this.setStrategyGap(section, id, strike - anchorStrike);
      }
      const prior = selected.expectedStrike === undefined ? selected.strike : selected.expectedStrike;
      let values: number[];
      if (group.some(r => r.strategyUseAdjacent) && selected.expiration) {
         values = this.adjacentStrategyStrikes(group, selected.expiration, strike, selected.strategyLeg);
      } else if (prior === null || prior === undefined) {
         if (group.some(r => r.strategyLeg > 0 && r.strategyGap === undefined)) {
            values = group.map(r => r.id === id ? strike : r.expectedStrike === undefined ? r.strike : r.expectedStrike);
            group.forEach((r, i) => {
               if (i && values[i] != null && values[i - 1] != null) { r.strategyGap = Math.abs(values[i] - values[i - 1]); }
            });
         } else {
            const direction = selected.optionType === 'Call' ? 1 : -1;
            const anchor = strike - direction * group.filter(r => r.strategyLeg > 0 && r.strategyLeg <= selected.strategyLeg)
               .reduce((n, r) => n + r.strategyGap, 0);
            values = [anchor];
            group.slice(1).forEach(r => values.push(values[values.length - 1] + direction * r.strategyGap));
         }
      } else {
         const delta = strike - prior;
         if (!delta) { return group; }
         values = group.map(r => {
            const value = r.expectedStrike === undefined ? r.strike : r.expectedStrike;
            return value == null ? null : value + delta;
         });
      }
      group = this.replaceHeldStrategy(section, group);
      this.applyStrategyStrikes(group, values);
      return group;
   }

   @UniqueOptionEdit
   setStrategyGap(section: PhaOptionSection, id: number, gap: number): PhaOptionRow[] {
      let group = this.editableStrategy(section, id);
      const outer = group.find(r => r.id === id);
      if (outer && outer.strategyType === 'Two-for-One' && isFinite(gap) && gap <= 0) {
         this.contractConflict = 'The Two-for-One Gap Must Be Positive.'; return [];
      }
      if (!outer || !outer.strategyLeg || !isFinite(gap) || gap < 0) { return []; }
      const oldGap = outer.strategyGap || 0;
      if (gap === outer.strategyGap) { return group; }
      const direction = outer.optionType === 'Call' ? 1 : -1;
      const values = group.map(r => {
         const value = r.expectedStrike === undefined ? r.strike : r.expectedStrike;
         return value === null || value === undefined ? null : value + (r.strategyLeg >= outer.strategyLeg ? direction * (gap - oldGap) : 0);
      });
      group = this.replaceHeldStrategy(section, group);
      group.forEach(r => r.strategyUseAdjacent = false);
      group.find(r => r.strategyLeg === outer.strategyLeg).strategyGap = gap;
      // Recover a missing intended strike from the known anchor and the edited point gaps.
      const anchor = values[0];
      if (anchor !== null) {
         group.slice(1).forEach((r, i) => values[i + 1] = values[i] !== null && isFinite(r.strategyGap) ? values[i] + direction * r.strategyGap : null);
         this.applyStrategyStrikes(group, values);
      }
      return group;
   }

   @UniqueOptionEdit
   setStrategyContracts(section: PhaOptionSection, id: number, contracts: number): void {
      const group = this.editableStrategy(section, id), row = group.find(r => r.id === id);
      if (!row || !isFinite(contracts)) { return; }
      if (row.strategyType === 'Two-for-One' && (contracts < 0 || !Number.isInteger(contracts / strategyRatio(row)))) {
         this.contractConflict = 'Two-for-One Quantities Must Be Whole 1:2 Units.'; return;
      }
      let units = Math.max(0, Math.floor(contracts / strategyRatio(row)));
      if (row.strategyType === 'Two-for-One' && units > (row as PhaPremiumRow).contractsSelling / strategyRatio(row)
         && units > this.freeStockUnits((row as PhaPremiumRow).underlyingRowId, row.strategyId)) {
         this.contractConflict = 'Insufficient Stock Backing for Two-for-One.'; return;
      }
      if (row.strategyType === 'Two-for-One') { group.forEach(r => (r as PhaPremiumRow).preserveInvalidBacking = false); }
      if (section === 'premium' && (row as PhaPremiumRow).pooled) {
         const premium = row as PhaPremiumRow;
         const others = this.premiumRows.filter(r => r.underlyingRowId === premium.underlyingRowId
            && r.strategyId !== row.strategyId && !r.crossedOut && !this.isExpired(r));
         const seen: {[key: string]: true} = {};
         const used = others.reduce((n, r) => {
            if (r.strategyId && seen[r.strategyId]) { return n; }
            if (r.strategyId) { seen[r.strategyId] = true; }
            return n + r.contractsSelling / strategyRatio(r);
         }, 0);
         const held = Math.max(...group.map(r => (r as PhaPremiumRow).heldContracts / strategyRatio(r)));
         units = Math.min(units, Math.max(held, Math.floor(premium.sharesHeld / 100) - used));
      }
      group.forEach(r => { if ('contractsSelling' in r) { r.contractsSelling = units * strategyRatio(r); }
         else { r.contracts = units * strategyRatio(r); } });
      this.recompute();
   }

   private reverseStrategy(section: PhaOptionSection, id: number, side: PhaSide): void {
      let group = this.editableStrategy(section, id), row = group.find(r => r.id === id);
      if (!row || row.side === side || PHA_SIDES.indexOf(side) < 0) { return; }
      if (row.strategyType === 'Two-for-One') { this.contractConflict = 'Two-for-One Uses One Long Call and Two Short Calls.'; return; }
      group = this.replaceHeldStrategy(section, group);
      group.forEach(r => r.side = r.side === 'Buy' ? 'Sell' : 'Buy');
      this.recompute();
   }

   @UniqueOptionEdit
   removeStrategy(section: PhaOptionSection, id: number): void {
      const group = this.editableStrategy(section, id);
      this.editingStrategyLifecycle = true;
      try { group.forEach(r => section === 'premium' ? this.removePremiumRow(r.id) : this.removeProtectionRow(r.id)); }
      finally { this.editingStrategyLifecycle = false; }
   }

   @UniqueOptionEdit
   restoreStrategy(section: PhaOptionSection, id: number): void {
      const group = this.strategyRows(section, id);
      if (group.some(r => this.isRowDisabled(r.id))) { return; }
      this.editingStrategyLifecycle = true;
      try { group.forEach(r => section === 'premium' ? this.restorePremiumRow(r.id) : this.restoreProtectionRow(r.id)); }
      finally { this.editingStrategyLifecycle = false; }
   }

   private readonly settlementPrices: {[ticker: string]: number | null} = {};

   isExpired(row: PhaUnderlyingRow | PhaPremiumRow | PhaProtectionRow): boolean {
      const ticker = 'assetType' in row ? (row.assetType === 'Call' ? row.ticker : null) : row.optionTicker;
      return !!ticker && this.optionExpired(ticker);
   }

   isRowDisabled(id: number): boolean {
      const row = [...this.rows, ...this.premiumRows, ...this.protectionRows].find(r => r.id === id);
      return this.isSessionLocked(id) || !!row && this.isExpired(row);
   }

   setSettlementPrice(ticker: string, price: number | null): void {
      this.settlementPrices[ticker] = price;
      this.recompute();
   }

   private settlementValue(ticker: string, type: PhaOptionType, strike: number): number | null {
      return optionSettlementValue(type, strike, this.settlementPrices[ticker]);
   }

   /** Import/restore contract facts without requiring a listed chain or invoking edit rules. */
   loadExpiredOption(row: PhaPremiumRow | PhaProtectionRow, expiration: PhaExpiration,
                     strike: number, ticker: string, contracts: number): void {
      row.expiration = expiration;
      row.strike = strike;
      row.optionTicker = ticker;
      row.removed = row.crossedOut = false;
      if ('contractsSelling' in row) {
         row.heldContracts = row.contractsSelling = contracts;
      } else {
         row.contracts = contracts;
      }
      this.recompute();
   }


   private optionHoldings: PhaOptionHoldings | null = null;
   private optionContributions: PhaOptionHoldings | null = null;

   /** Book ownership follows the session baseline, including acquisitions made while other orders remain. */
   reconcileOptionHoldings(holdings: PhaOptionHoldings, contributions?: PhaOptionHoldings): void {
      this.optionContributions = contributions || null;
      const reconcile = (rows: Array<PhaPremiumRow | PhaProtectionRow>, section: string) => {
         const groups: {[ticker: string]: Array<PhaPremiumRow | PhaProtectionRow>} = {};
         rows.forEach(row => {
            if (row.optionTicker) {
               const key = row.optionTicker + (contributions && row.strategyId ? '|strategy:' + row.strategyId : '');
               (groups[key] || (groups[key] = [])).push(row);
            }
         });
         Object.keys(groups).forEach(ticker => {
            const quantity = contributions
               ? contributions[optionContributionKey(section, groups[ticker][0].optionTicker, groups[ticker][0].strategyId)] || 0
               : holdings[section + '|' + ticker] || 0;
            if (!quantity) {
               if (contributions) { groups[ticker].forEach(row => {
                  if ('heldContracts' in row) { row.heldContracts = 0; }
                  if (!row.crossedOut) { row.origin = 'new'; }
               }); }
               return;
            }
            const side: PhaSide = quantity < 0 ? 'Sell' : 'Buy';
            const matching = groups[ticker].filter(row => row.side === side);
            // Keep an existing close row as the owner during a same-contract reversal. Otherwise
            // start with a matching design row, then distribute the book quantity across duplicates.
            const owner = matching.find(row => row.origin === 'held')
               || matching.find(row => !row.crossedOut) || matching[0];
            if (!owner) { return; }
            groups[ticker].forEach(row => {
               if ('heldContracts' in row) {
                  row.heldContracts = 0;
               }
            });
            // Distribute the book once across matching rows, retaining held rows first. This
            // protects acquired duplicate rows from the Premium pool without duplicating holdings.
            let remaining = Math.abs(quantity);
            const ordered = [owner, ...matching.filter(row => row !== owner)];
            ordered.forEach(row => {
               const desired = 'contractsSelling' in row ? row.contractsSelling : row.contracts;
               const allocated = Math.min(remaining, desired);
               if (!allocated) { return; }
               row.origin = 'held';
               if ('heldContracts' in row) { row.heldContracts = allocated; }
               remaining -= allocated;
            });
            if (remaining) {
               owner.origin = 'held';
               if ('heldContracts' in owner) { owner.heldContracts += remaining; }
            }
         });
      };
      reconcile(this.premiumRows, 'Premium');
      reconcile(this.protectionRows, 'Protection');
   }

   /** Refresh derived amounts only; never mutate the design or the export quantities. */
   setOptionHoldings(holdings: PhaOptionHoldings): void {
      this.optionHoldings = holdings;
      this.recomputePremium();
      this.recomputeProtection();
      this.recomputeNetHedge();
   }

   private _seq = 0;

   /** Entered/imported valuation reference; quote ticks never rewrite the allocation reference. */
   accountValue: number | null = null;
   private _accountCash: number | null = null;
   private _accountPositions: {[id: number]: PhaAccountPosition} = {};
   private _accountPrices: {[id: number]: number} = {};
   stockAllocationPercent = 100;

   /** @deprecated Kept for restoring and compiling pre-account-value analysis clients. */
   get accountSize(): number | null { return this.accountValue; }
   set accountSize(value: number | null) { this.setAccountValue(value); }

   /** Establish an entered/imported equity reference and the corresponding cash balance. */
   setAccountValue(value: number | null): void {
      this.accountValue = value === undefined || value === null || !isFinite(value) ? null : value;
      this._accountCash = this.accountValue === null ? null : this.accountValue - this.accountMarketValue;
      this.recompute();
   }

   /** Current equity: cash plus the latest marks, including cash released by design sells. */
   get currentAccountValue(): number | null {
      if (!this.isUnlocked || this._accountCash === null
         || Object.keys(this._accountPositions).some(id => this._accountPositions[+id].needsPointValueRebase)
         || this.countingRows.some(r => r.shares > 0 && (r.price === null || this.pointValue(r) === null || !this._accountPositions[r.id]))) { return null; }
      return round2(this._accountCash + this.accountMarketValue);
   }

   private get accountMarketValue(): number {
      return Object.keys(this._accountPositions).reduce((sum, id) => {
         const position = this._accountPositions[+id];
         return sum + position.quantity * (this._accountPrices[+id] === undefined ? position.unitPrice : this._accountPrices[+id]);
      }, 0);
   }

   get accountValuation(): PhaAccountValuation {
      return {cash: this._accountCash, positions: Object.keys(this._accountPositions).map(id => ({...this._accountPositions[+id]}))};
   }

   /** Restore accounting marks, not live quote controls; subsequent quotes revalue the same shares. */
   restoreAccountValuation(value: PhaAccountValuation, legacy = false): void {
      if (!value || typeof value.cash !== 'number' || !isFinite(value.cash) || !Array.isArray(value.positions)) { return; }
      const positions: {[id: number]: PhaAccountPosition} = {};
      for (const position of value.positions) {
         const row = this.rows.find(r => r.ticker === position.ticker);
         if (!row || typeof position.quantity !== 'number' || !isFinite(position.quantity) || position.quantity < 0
            || typeof position.unitPrice !== 'number' || !isFinite(position.unitPrice) || position.unitPrice < 0) { return; }
         positions[row.id] = {...position, pointValue: position.pointValue ?? (legacy ? (row.assetType === 'Call' ? 100 : 1) : this.pointValue(row)),
            needsPointValueRebase: !!position.needsPointValueRebase || (legacy && row.assetType === 'Future')};
      }
      this._accountCash = value.cash;
      this._accountPositions = positions;
      this._accountPrices = {};
      this.recompute();
   }

   private recomputeAccountValuation(): void {
      if (this._accountCash === null) { return; }
      // Quantities change cash at the current mark. Quote changes only revalue holdings.
      this.rows.forEach(row => {
         const previous = this._accountPositions[row.id];
         const multiplier = this.pointValue(row);
         if (multiplier === null) { return; }
         if (previous?.needsPointValueRebase && row.price === null) { return; }
         if (previous && (previous.needsPointValueRebase || previous.pointValue > 0 && previous.pointValue !== multiplier)) {
            // Legacy state saved contract dollars without their quoted reference price. Rebase at
            // the first valid mark, preserving its reference equity instead of inventing P&L.
            const corrected = previous.needsPointValueRebase ? this.unitPrice(row)
               : previous.unitPrice * multiplier / previous.pointValue;
            this._accountCash -= previous.quantity * (corrected - previous.unitPrice);
            previous.unitPrice = corrected;
            previous.pointValue = multiplier;
            previous.needsPointValueRebase = false;
            delete this._accountPrices[row.id];
         }
         const quantity = row.crossedOut ? 0 : row.shares;
         const price = row.price !== null && isFinite(row.price) && row.price >= 0 ? this.unitPrice(row)
            : this._accountPrices[row.id] !== undefined ? this._accountPrices[row.id] : previous ? previous.unitPrice : null;
         if (price === null) { return; }
         this._accountCash -= (quantity - (previous ? previous.quantity : 0)) * price;
         this._accountPrices[row.id] = price;
         // Keep quote-only ticks out of persisted design changes; save cash-transfer reference marks.
         this._accountPositions[row.id] = {ticker: row.ticker, quantity,
            unitPrice: previous && quantity === previous.quantity ? previous.unitPrice : price, pointValue: multiplier};
      });
      Object.keys(this._accountPositions).forEach(id => {
         if (!this.rows.some(row => row.id === +id)) {
            const previous = this._accountPositions[+id];
            this._accountCash += previous.quantity * (this._accountPrices[+id] === undefined ? previous.unitPrice : this._accountPrices[+id]);
            delete this._accountPositions[+id];
            delete this._accountPrices[+id];
         }
      });
   }

   /** @deprecated Legacy method alias. */
   setAccountSize(value: number | null): void { this.setAccountValue(value); }

   setStockAllocationPercent(value: number | null): void {
      this.stockAllocationPercent = typeof value === 'number' && isFinite(value) ? Math.max(0, Math.min(100, value)) : 100;
      this.recompute();
   }

   get maxStockValue(): number | null {
      return this.accountValue !== null && this.accountValue > 0
         ? this.accountValue * this.stockAllocationPercent / 100
         : null;
   }

   /** Cash available after keeping the reference reserve aside; negative means overallocated. */
   get availableInvestment(): number | null {
      return this.currentAccountValue === null || this.reserve === null ? null : round2(this._accountCash - this.reserve);
   }

   /** Account money deliberately kept outside the stock allocation. */
   get reserve(): number | null {
      return this.maxStockValue === null ? null : round2(this.accountValue - this.maxStockValue);
   }

   /** Reserve dollars as a share of the current account value. */
   get reservePercent(): number | null {
      const dollars = this.reserve;
      const account = this.currentAccountValue;
      return account !== null && account > 0 && dollars !== null
         ? dollars / account * 100
         : null;
   }

   /** Null for a standalone analysis. */
   portfolio: PhaPortfolioContext | null = null;

   rows: PhaUnderlyingRow[] = [];

   totals: PhaTotals = {dollars: 0, pct: 0, betaDelta: null, freeCash: null, pctConsistent: true, dollarsConsistent: true};

   /** The balance button pressed last; stays highlighted so the trader knows what mode the sheet is in (section 5). */
   lastBalance: PhaBalanceMode | null = null;
   roundingDeviationPercent = 20;

   /**
    * The rows whose order block is copied to ToS and not yet acquired (CR-20260930-001 M2, §6.7 —
    * set by the panel from the session): FROZEN. A balance move keeps their shares (off the top, as
    * a shares-locked row); the OTM movers skip them. Their editors are disabled in the tables.
    */
   sessionLockedRowIds: {[rowId: number]: true} = {};

   isSessionLocked(rowId: number): boolean {
      if (this.sessionLockedRowIds[rowId]) { return true; }
      const row = [...this.premiumRows, ...this.protectionRows].find(r => r.id === rowId);
      return !!row && !!row.strategyId && [...this.premiumRows, ...this.protectionRows]
         .some(r => r.strategyId === row.strategyId && !!this.sessionLockedRowIds[r.id]);
   }

   // ---- Premium (M3) ----
   premiumRows: PhaPremiumRow[] = [];

   // The Premium "global % OTM" absolute setter is GONE (owner, 2026-09-29 vrequest #2, ruling R15):
   // a new row resolves at PHA_DEFAULT_PCT_OTM, each row's % OTM is typed/spun on the row itself,
   // and each section's header mover SHIFTS every row by a delta. Nothing absolute survives.

   premiumTotals: PhaPremiumTotals = {dollars: 0, dollarsExisting: 0, dollarsTransaction: 0, unpricedRows: 0};

   // ---- Protection (M4) ----
   protectionRows: PhaProtectionRow[] = [];

   protectionTotals: PhaProtectionTotals = {dollars: 0, dollarsExisting: 0, dollarsTransaction: 0, dollarsProtected: 0, unpricedRows: 0};

   /** Spot prices of instruments that are not held (SPX, XSP, SPY on Protection rows and index Premium rows): fed by the panel. */
   protectionSpots: {[symbol: string]: number} = {};

   netHedge: PhaNetHedge = {premium: 0, protection: 0, net: 0};

   get isUnlocked(): boolean {
      return this.accountValue !== null && this.accountValue !== undefined && this.accountValue > 0;
   }

   /** Rows that count: everything not crossed out. */
   get countingRows(): PhaUnderlyingRow[] {
      return this.rows.filter(r => !r.crossedOut);
   }

   hasTicker(ticker: string): boolean {
      return this.rows.some(r => r.ticker === ticker);
   }

   /**
    * Add an underlying with the default quantity of 100 shares (§5 "Adding an underlying"). Nothing
    * else moves: the new row takes whatever share its 100 shares are worth once priced. One row per
    * ticker per analysis (§11 assumption); a duplicate returns the existing row and adds nothing.
    */
   addUnderlying(ticker: string, displayName: string, origin: PhaRowOrigin = 'new', shares = 100,
                 sourcePositionId?: string, assetType: PhaUnderlyingAssetType = 'Stock'): PhaUnderlyingRow {
      const existing = this.rows.find(r => r.ticker === ticker);
      if (existing) {
         return existing;
      }
      const row: PhaUnderlyingRow = {
         id: ++this._seq,
         ticker,
         displayName: displayName || ticker,
         origin,
         side: 'Buy',
         assetType: this.resolveUnderlyingType(ticker) || assetType,
         sourcePositionId,
         crossedOut: false,
         selected: false,
         shares,
         price: null,
         priceStale: false,
         beta: null,
         pctOverride: null,
         sharesLock: false,
         pctLock: false,
         dollars: null,
         pct: null,
         betaDelta: null
      };
      this.rows.push(row);
      this.recompute();
      return row;
   }

   /** Add/restore one long call. Entry-time ITM validation belongs to the dialog/panel. */
   @UniqueOptionEdit
   addCallUnderlying(optionTicker: string, underlying: string, strike: number, expirationDate: string,
                     expirationTicker: string, contracts: number, origin: PhaRowOrigin = 'new',
                     sourcePositionId?: string): PhaUnderlyingRow {
      const existing = this.rows.find(r => r.ticker === optionTicker);
      if (existing) { return existing; }
      if (!optionTicker || !underlying || !(strike > 0) || !expirationDate
         || !Number.isInteger(contracts) || contracts < 0) { return null; }
      const row = this.addUnderlying(optionTicker, `${underlying} ${expirationDate} ${strike} Call`, origin, contracts, sourcePositionId);
      row.assetType = 'Call';
      row.callUnderlying = underlying;
      row.callStrike = strike;
      row.callExpirationDate = expirationDate;
      row.callExpirationTicker = expirationTicker;
      row.callDelta = 1;
      row.side = 'Buy';
      this.recompute();
      return row;
   }

   /** Dollars per quoted point come from the instrument, never from the ticker spelling. */
   pointValue(row: PhaUnderlyingRow): number | null {
      const value = this.resolvePointValue(row.ticker);
      if (value != null) { return isFinite(value) && value > 0 ? value : null; }
      return row.assetType === 'Future' ? null : row.assetType === 'Call' ? 100 : 1;
   }
   private optionPointValue(ticker: string): number | null {
      const value = this.resolvePointValue(ticker);
      return value == null ? 100 : isFinite(value) && value > 0 ? value : null;
   }
   private unitPrice(row: PhaUnderlyingRow): number { return row.price * this.pointValue(row); }
   private unitBeta(row: PhaUnderlyingRow): number {
      return row.beta * this.pointValue(row) * (row.assetType === 'Call' ? row.callDelta : 1);
   }

   /**
    * Open from a portfolio (section 5 "Opening"): one HELD row per held name with the portfolio's
   * share count; Premium and Protection start from a blank slate; account value is supplied by the panel
    * pre-filled. Replaces whatever the sheet held ([my call]: choosing a portfolio starts a new
    * analysis on it). A long position only; shorts are refused by the caller (section 6).
    */
   loadPortfolio(context: PhaPortfolioContext | null, held: PhaHeldPosition[]): void {
      this.portfolio = context;
      this._accountCash = this.accountValue;
      this._accountPositions = {};
      this._accountPrices = {};
      this.rows = [];
      this.premiumRows = [];
      this.protectionRows = [];
      this.protectionSpots = {};
      this.lastBalance = null;
      this.setStockAllocationPercent(100);
      (held || []).forEach(h => {
         if (h.shares > 0 && !this.hasTicker(h.ticker)) {
            this.addUnderlying(h.ticker, h.displayName, 'held', h.shares, h.positionId, h.assetType);
         }
      });
      this.recompute();
   }

   /**
    * Remove (§5 "Removing an underlying"): a NEW row is deleted; a HELD row is crossed out and stays
    * as the record of "this is to go". Returns what happened so the caller can swap the X for the
    * restore control.
    */
   @UniqueOptionEdit
   removeUnderlying(id: number): 'deleted' | 'crossedOut' | 'none' {
      if (this.isRowDisabled(id)) { return 'none'; }
      const row = this.rows.find(r => r.id === id);
      if (!row) {
         return 'none';
      }
      if (row.origin === 'held') {
         row.crossedOut = true;
         this.recompute();
         return 'crossedOut';
      }
      this.rows = this.rows.filter(r => r.id !== id);
      this.recompute();
      return 'deleted';
   }

   /** The row's SIDE picked (item 4). A label only on an Underlying row: no arithmetic changes in v1. */
   @UniqueOptionEdit
   setUnderlyingSide(id: number, side: PhaSide): void {
      if (this.isRowDisabled(id)) { return; }
      const row = this.rows.find(r => r.id === id);
      if (row && row.assetType !== 'Call' && PHA_SIDES.indexOf(side) >= 0) {
         row.side = side;
      }
   }

   /** Restore a crossed-out held row: back in play, no confirmation (§5). */
   @UniqueOptionEdit
   restoreUnderlying(id: number): void {
      if (this.isRowDisabled(id)) { return; }
      const row = this.rows.find(r => r.id === id);
      if (row && row.crossedOut) {
         row.crossedOut = false;
         this.recompute();
      }
   }

   /**
    * Typed shares (§5 "Editing shares or percent by hand"): the row's dollars change and every row's
    * % follows from dollars, so the column totals 100 again. [my call] a typed share count clears
    * EVERY pending % request, because the description says the column "still totals 100" after it —
    * which is only true if no request is left standing. Shares are whole numbers: a fractional
    * value (the input box already blocks one) is floored.
    */
   @UniqueOptionEdit
   setShares(id: number, shares: number): void {
      if (this.isRowDisabled(id)) { return; }
      const row = this.rows.find(r => r.id === id);
      if (!row || !isFinite(shares) || shares < 0) {
         return;
      }
      row.shares = Math.floor(shares);
      this.rows.filter(r => !this.isExpired(r)).forEach(r => r.pctOverride = null);
      this.recompute();
   }

   /**
    * Typed % (§5): a REQUEST. It stands as typed; the other rows are not cut; the total now reads
    * something other than 100 and is flagged red until a balance move (M2) or a manual fix.
    */
   setPercentRequest(id: number, pct: number): void {
      if (this.isRowDisabled(id)) { return; }
      const row = this.rows.find(r => r.id === id);
      if (!row || !isFinite(pct) || pct < 0) {
         return;
      }
      row.pctOverride = pct;
      this.recompute();
   }

   /** The two locks exclude each other ([my call], spec §2): engaging one releases the other. */
   toggleSharesLock(id: number): void {
      if (this.isRowDisabled(id)) { return; }
      const row = this.rows.find(r => r.id === id);
      if (!row) {
         return;
      }
      row.sharesLock = !row.sharesLock;
      if (row.sharesLock) {
         row.pctLock = false;
      }
   }

   togglePctLock(id: number): void {
      if (this.isRowDisabled(id)) { return; }
      const row = this.rows.find(r => r.id === id);
      if (!row) {
         return;
      }
      row.pctLock = !row.pctLock;
      if (row.pctLock) {
         row.sharesLock = false;
      }
   }

   /** A price tick for one ticker: that row and the totals recompute; nothing else (§3). */
   setPrice(ticker: string, price: number | null, stale = false): boolean {
      let touched = false;
      this.rows.forEach(r => {
         if (r.ticker === ticker) {
            r.price = price;
            r.priceStale = stale;
            touched = true;
         }
      });
      if (touched) {
         this.recompute();
      }
      return touched;
   }

   setBeta(ticker: string, beta: number | null): void {
      let touched = false;
      this.rows.forEach(r => {
         if (r.ticker === ticker) {
            r.beta = beta;
            touched = true;
         }
      });
      if (touched) {
         this.recompute();
      }
   }

   // ---- Premium (section 5 "Building the Premium section") -------------------------------------

   /** Populate: one Premium row for every Underlying row that has none yet (crossed-out rows too, crossed out). */
   populatePremium(): PhaPremiumRow[] {
      const added: PhaPremiumRow[] = [];
      this.rows.filter(u => u.assetType !== 'Call').forEach(u => {
         if (!this.premiumRows.some(p => p.underlyingRowId === u.id)) {
            added.push(this.addPremiumRow(u.id));
         }
      });
      return added;
   }

   /**
    * What a Premium row's name selector offers (owner, 2026-09-29): every held name (each Underlying
    * row, keyed by its id) and then the index options (the R2 list) not already held, keyed by ticker.
    */
   premiumChoices(): PhaPremiumChoice[] {
      const out: PhaPremiumChoice[] = this.rows.filter(u => u.assetType !== 'Call').map(u => ({
         key: premiumChoiceKeyFor(u.id, underlyingSymbol(u)), underlyingRowId: u.id, ticker: underlyingSymbol(u), displayName: u.displayName
      }));
      PHA_PROTECTION_LIST.forEach(t => {
         if (!this.hasTicker(t)) {
            out.push({key: premiumChoiceKeyFor(null, t), underlyingRowId: null, ticker: t, displayName: t});
         }
      });
      // A row already sitting on a name that is neither held nor on the list (a loaded option whose
      // stock is not in the combo — M2 smoke: the selector showed BLANK for it) keeps its name on offer.
      this.premiumRows.filter(p => !p.pooled).forEach(p => {
         if (!out.some(c => c.underlyingRowId === null && c.ticker === p.ticker)) {
            out.push({key: premiumChoiceKeyFor(null, p.ticker), underlyingRowId: null, ticker: p.ticker, displayName: p.displayName || p.ticker});
         }
      });
      return out;
   }

   /**
    * One more Premium row on an underlying (the same ticker may appear more than once: expirations,
    * strikes). A new row is a SELL CALL; a row loaded from a portfolio (item 10) brings its own type and side.
    */
   addPremiumRow(underlyingRowId: number, optionType: PhaOptionType = 'Call', side: PhaSide = 'Sell',
                 origin: PhaRowOrigin = 'new'): PhaPremiumRow {
      const u = this.rows.find(r => r.id === underlyingRowId);
      if (!u) {
         return null;
      }
      const row = this.newPremiumRow(u.id, underlyingSymbol(u), u.displayName, u.crossedOut, backingShares(u), optionType, side, origin);
      this.premiumRows.push(row);
      this.recompute();
      return row;
   }

   /** One more Premium row on an index option (not held: no pool, contracts typed freely). */
   addIndexPremiumRow(ticker: string, optionType: PhaOptionType = 'Call', side: PhaSide = 'Sell',
                      origin: PhaRowOrigin = 'new'): PhaPremiumRow {
      if (!ticker) {
         return null;
      }
      const row = this.newPremiumRow(null, ticker, ticker, false, 0, optionType, side, origin);
      this.premiumRows.push(row);
      this.recompute();
      return row;
   }

   /**
    * The row's SIDE picked (item 4): the sign of its dollars follows (SELL = credit, BUY = debit).
    * On a HELD row a side change REVERSES the position (owner, 2026-10-01 vrequest, the Hedge Pad
    * rule): the held row is crossed out — the close — and a NEW row with the opposite side opens.
    * A NEW row edits in place; a session-locked or crossed-out held row refuses.
    */
   @UniqueOptionEdit
   setPremiumSide(id: number, side: PhaSide): void {
      if (this.premiumRows.some(r => r.id === id && r.strategyId)) { this.reverseStrategy('premium', id, side); return; }
      const p = this.premiumRows.find(r => r.id === id);
      if (!p || PHA_SIDES.indexOf(side) < 0 || side === p.side || this.isRowDisabled(id)) {
         return;
      }
      if (p.origin === 'held') {
         const clone = this.splitHeldPremiumRow(p);
         if (clone === p) {
            return;
         }
         clone.side = side;
      } else {
         p.side = side;
      }
      this.recompute();
   }

   /**
    * The Hedge Pad identity rule (owner, 2026-10-01 vrequest): an edit that changes a HELD option
    * row's identity — its side (a reversal), its expiration or its strike (another contract) —
    * CLOSES the held row and OPENS a new one. The held row is crossed out (restorable, kept priced
    * so its close shows in the transaction column); a clone (`origin: 'new'`) carries the edit and
    * is returned. A NEW row has no base to change against and edits in place; a session-locked or
    * already crossed-out row is REFUSED (the same row comes back — the caller must not apply the
    * edit to it). The initial contract fill from null at import / state restore is not an identity
    * change: the callers branch on that before calling.
    */
   private splitHeldPremiumRow(p: PhaPremiumRow): PhaPremiumRow {
      if (p.origin !== 'held' || p.removed || p.crossedOut || this.isRowDisabled(p.id)) {
         return p;
      }
      p.removed = true;
      // The clone keeps everything, the quote fields included: a side reversal is the SAME contract
      // and prices at once; an expiration / strike re-pick clears them right after (the panel then
      // rebuilds the ticker and re-subscribes).
      const clone: PhaPremiumRow = Object.assign({}, p, {
         id: ++this._seq,
         origin: 'new' as PhaRowOrigin,
         removed: false,
         crossedOut: false,
         heldContracts: 0,
         dollars: null
      });
      this.premiumRows.push(clone);
      return clone;
   }

   private newPremiumRow(underlyingRowId: number | null, ticker: string, displayName: string, crossedOut: boolean,
                         sharesHeld: number, optionType: PhaOptionType, side: PhaSide, origin: PhaRowOrigin): PhaPremiumRow {
      const pooled = underlyingRowId !== null;
      return {
         id: ++this._seq,
         side,
         optionType,
         underlyingRowId,
         pooled,
         ticker,
         displayName,
         origin,
         removed: false,
         crossedOut,
         sharesHeld,
         contractsAvailable: pooled ? Math.floor(sharesHeld / 100) : 0,
         contractsSelling: 0,
         heldContracts: 0,
         expirations: [],
         expiration: null,
         strike: null,
         pctOtm: null,
         otmLock: false,
         optionTicker: null,
         bid: null,
         ask: null,
         feedMid: null,
         feedLast: null,
         price: null,
         netStrategyPrice: null,
         dollars: null
      };
   }

   /**
    * Remove (owner, 2026-09-29 smoke 3 — the Underlyings rule for every table): a HELD row is crossed
    * out and stays, restorable; a NEW row is deleted. Returns what happened.
    */
   removePremiumRow(id: number): 'deleted' | 'crossedOut' | 'none' {
      if (this.isRowDisabled(id)) { return 'none'; }
      const p = this.premiumRows.find(r => r.id === id);
      if (!p) {
         return 'none';
      }
      if (p.strategyType === 'Two-for-One' && !this.editingStrategyLifecycle) {
         this.removeStrategy('premium', id); return p.origin === 'held' ? 'crossedOut' : 'deleted';
      }
      if (p.origin === 'held') {
         p.removed = true;
         this.recompute();
         return 'crossedOut';
      }
      this.premiumRows = this.premiumRows.filter(r => r.id !== id);
      this.recompute();
      return 'deleted';
   }

   /** Restore a crossed-out held Premium row (its own cross-out only; the underlying's still applies). */
   @UniqueOptionEdit
   restorePremiumRow(id: number): void {
      if (this.isRowDisabled(id)) { return; }
      const p = this.premiumRows.find(r => r.id === id);
      if (p && p.removed) {
         if (p.strategyType === 'Two-for-One' && !this.editingStrategyLifecycle) { this.restoreStrategy('premium', id); return; }
         p.removed = false;
         this.recompute();
      }
   }

   /**
    * Re-point a Premium row at another Underlying row (the "name" selection). Resets the option.
    * A HELD row refuses (owner, 2026-10-01): the underlying it came from is a fact — only NEW
    * rows are re-pointed.
    */
   @UniqueOptionEdit
   setPremiumUnderlying(id: number, underlyingRowId: number): void {
      if (this.isRowDisabled(id)) { return; }
      const u = this.rows.find(r => r.id === underlyingRowId);
      const p = this.premiumRows.find(r => r.id === id);
      if (p && p.strategyType === 'Two-for-One' && this.strategyRows('premium', id).length > 1) {
         const choice = this.premiumChoices().find(c => c.underlyingRowId === underlyingRowId);
         if (choice) { this.repointOptionStrategy('premium', id, choice.key); }
         return;
      }
      if (!u || u.assetType === 'Call' || !p || p.origin === 'held') {
         return;
      }
      this.repointPremium(id, u.id, underlyingSymbol(u), u.displayName, u.crossedOut, backingShares(u));
   }

   /** Re-point a Premium row at an index option (not held). A HELD row refuses (see setPremiumUnderlying). */
   @UniqueOptionEdit
   setPremiumIndex(id: number, ticker: string): void {
      if (this.isRowDisabled(id)) { return; }
      const p = this.premiumRows.find(r => r.id === id);
      if (p && p.strategyType === 'Two-for-One') { this.contractConflict = 'Two-for-One Requires Stock Backing.'; return; }
      if (!ticker || !p || p.origin === 'held') {
         return;
      }
      this.repointPremium(id, null, ticker, ticker, false, 0);
   }

   /** The name selector's pick, by its choice key (see premiumChoices). */
   @UniqueOptionEdit
   setPremiumChoice(id: number, key: string): void {
      const c = this.premiumChoices().find(x => x.key === key);
      if (!c) {
         return;
      }
      if (c.underlyingRowId !== null) {
         this.setPremiumUnderlying(id, c.underlyingRowId);
      } else {
         this.setPremiumIndex(id, c.ticker);
      }
   }

   private repointPremium(id: number, underlyingRowId: number | null, ticker: string, displayName: string,
                          crossedOut: boolean, sharesHeld: number, recompute = true): void {
      const p = this.premiumRows.find(r => r.id === id);
      if (!p) {
         return;
      }
      p.underlyingRowId = underlyingRowId;
      p.pooled = underlyingRowId !== null;
      p.ticker = ticker;
      p.displayName = displayName;
      p.crossedOut = p.removed || crossedOut;
      p.sharesHeld = sharesHeld;
      p.expirations = [];
      p.expiration = null;
      this.clearPremiumOption(p);
      if (recompute) { this.recompute(); }
   }

   /** The chain arrived for a ticker: every Premium row on it gets the listed expirations. */
   setPremiumExpirations(ticker: string, expirations: PhaExpiration[]): void {
      this.premiumRows.filter(p => p.ticker === ticker).forEach(p => p.expirations = expirations || []);
   }

   /**
    * Expiration selected: the strike is resolved at once ([my call]: a selected expiration with no
    * strike is unpriced; resolving it saves a click and the trader may re-select). On a HELD row the
    * pick is an identity change (owner, 2026-10-01 vrequest): the held row is crossed out and a NEW
    * row takes the expiration, its strike resolved at the HELD row's % OTM — "the same strike, the
    * same possible OTM-ness" (a new row resolves at the default). Returns the row (the new one when
    * split) so the panel can build the option ticker and subscribe its quote. The optional creation
    * offset applies only when the row has no actual OTM yet; manual callers retain the legacy default.
    */
   @UniqueOptionEdit
   setPremiumExpiration(id: number, expiration: PhaExpiration | null,
                        creationPctOtm: number = PHA_DEFAULT_PCT_OTM): PhaPremiumRow {
      const linked = this.premiumRows.find(r => r.id === id && r.strategyId);
      if (linked) { return this.setStrategyExpiration('premium', id, expiration).find(r => r.strategyLeg === linked.strategyLeg) as PhaPremiumRow || linked; }
      const p = this.premiumRows.find(r => r.id === id);
      if (!p) {
         return null;
      }
      if (this.isRowDisabled(id)) {
         return p;
      }
      if (p.expiration === expiration || (p.expiration && expiration && p.expiration.date === expiration.date)) {
         return p;                                     // the same expiration picked again
      }
      // A RE-PICK on a HELD row (it already carries an expiration) is an identity change: the held
      // row is crossed out and a NEW row takes the pick, its strike resolved at the HELD row's
      // % OTM — "the same strike, the same possible OTM-ness" (a new row resolves at the default).
      // The initial fill from null (the import, the state restore) is not an identity change.
      let target = p;
      if (p.origin === 'held' && p.expiration !== null) {
         target = this.splitHeldPremiumRow(p);
         if (target === p) {
            return p;                                  // session-locked / crossed out: refused
         }
      }
      target.expiration = expiration;
      this.clearPremiumOption(target);
      if (expiration) {
         this.resolvePremiumStrike(target, target.pctOtm !== null ? target.pctOtm : clampPctOtm(creationPctOtm));
      }
      this.recompute();
      return target;
   }

   /**
    * Strike selected by hand: stays until the row's % OTM is edited or the section's mover is
    * stepped. On a HELD row an identity change (owner, 2026-10-01 vrequest): the held row is
    * crossed out, a NEW row takes the strike. Returns the row (the new one when split).
    */
   @UniqueOptionEdit
   setPremiumStrike(id: number, strike: number | null): PhaPremiumRow {
      const linked = this.premiumRows.find(r => r.id === id && r.strategyId);
      if (linked) { return strike === null ? linked : this.moveStrategy('premium', id, strike).find(r => r.strategyLeg === linked.strategyLeg) as PhaPremiumRow || linked; }
      const p = this.premiumRows.find(r => r.id === id);
      if (!p) {
         return null;
      }
      if (this.isRowDisabled(id)) {
         return p;
      }
      if (p.strike === strike) {
         return p;                                     // the same strike picked again
      }
      // A RE-PICK on a HELD row that already trades a contract (ticker set) is an identity change
      // (owner, 2026-10-01 vrequest): the held row is crossed out, a NEW row takes the pick. The
      // initial fill from null (the import, the state restore — the ticker is built only after)
      // is not an identity change, even though the expiration pick auto-resolved a strike.
      let target = p;
      if (p.origin === 'held' && p.strike !== null && p.optionTicker !== null) {
         target = this.splitHeldPremiumRow(p);
         if (target === p) {
            return p;                                  // session-locked / crossed out: refused
         }
      }
      this.clearPremiumOption(target);
      target.strike = strike;
      this.recompute();
      return target;
   }

   /** The panel tells the sheet the option ticker it built for the row's (expiration, strike). */
   @UniqueOptionEdit
   setPremiumOptionTicker(id: number, optionTicker: string | null): void {
      const p = this.premiumRows.find(r => r.id === id);
      if (p) {
         p.optionTicker = optionTicker;
      }
   }

   setPremiumContracts(id: number, contracts: number): void {
      if (this.premiumRows.some(r => r.id === id && r.strategyId)) { this.setStrategyContracts('premium', id, contracts); return; }
      if (this.isRowDisabled(id)) { return; }
      const p = this.premiumRows.find(r => r.id === id);
      if (!p || p.crossedOut) {
         return;
      }
      const n = Math.floor(isFinite(contracts) ? contracts : 0);
      p.contractsSelling = p.pooled ? Math.max(0, Math.min(Math.max(this.poolLeftFor(p), p.heldContracts), n)) : Math.max(0, n);
      this.recompute();
   }

   /**
    * A HELD row's count as the book holds it (the import, the restore): set as is, never clamped —
    * the pool then never cuts the row below it (see `heldContracts`).
    */
   setPremiumHeldContracts(id: number, contracts: number): void {
      if (this.isRowDisabled(id)) { return; }
      const p = this.premiumRows.find(r => r.id === id);
      if (!p) {
         return;
      }
      const n = Math.max(0, Math.floor(isFinite(contracts) ? contracts : 0));
      p.heldContracts = n;
      p.contractsSelling = n;
      this.recompute();
   }

   /** The underlying's pool (floor(shares/100)) less what the row's siblings sell. Pooled rows only. */
   private poolLeftFor(p: PhaPremiumRow): number {
      const u = this.rows.find(r => r.id === p.underlyingRowId);
      const pool = u && !this.isExpired(u) ? Math.floor(backingShares(u) / 100) : 0;
      const seen: {[id: string]: true} = {};
      const siblings = this.premiumRows
         .filter(o => o.underlyingRowId === p.underlyingRowId && o.id !== p.id && !o.crossedOut && !this.isExpired(o))
         .reduce((sum, o) => {
            if (o.strategyId && seen[o.strategyId]) { return sum; }
            if (o.strategyId) { seen[o.strategyId] = true; }
            return sum + o.contractsSelling / strategyRatio(o);
         }, 0);
      return Math.max(0, pool - siblings);
   }

   /**
    * The row's % OTM typed or spun (R15): the strike re-selects to the listed strike nearest that %
    * from spot — negative = in the money, the strike on the other side of spot (owner, 2026-09-29).
    * A strike change on a HELD row that already trades a contract is an IDENTITY change (owner,
    * 2026-10-01, smoke: the OTM mechanics are a strike re-pick like any other): the held row is
    * crossed out and a NEW row takes the resolved strike. When the nearest listed strike does not
    * move, nothing splits. Returns the row (the new one when split) so the panel can re-subscribe.
    */
   @UniqueOptionEdit
   setPremiumPctOtm(id: number, pct: number): PhaPremiumRow {
      const p = this.premiumRows.find(r => r.id === id);
      if (!p || !p.expiration || !isFinite(pct) || this.isRowDisabled(id)) {
         return p || null;
      }
      const resolved = resolveStrikeByType(p.optionType, p.expiration, this.spotOf(p.ticker), clampPctOtm(pct));
      if (p.strategyId) {
         return resolved === null ? p : this.moveStrategy('premium', id, resolved).find(r => r.strategyLeg === p.strategyLeg) as PhaPremiumRow || p;
      }
      if (resolved === null || resolved === p.strike) {
         return p;                                     // the same strike: no contract change, no split
      }
      const target = this.splitForPctOtm(p);
      if (target === p && p.origin === 'held') {
         return p;                                     // a crossed-out / locked held row: no effect
      }
      this.clearPremiumQuote(target);
      target.strike = resolved;
      this.recompute();
      return target;
   }

   /** The identity split behind a % OTM edit: a HELD row trading a contract splits, the rest edit in place. */
   private splitForPctOtm(p: PhaPremiumRow): PhaPremiumRow {
      if (p.origin === 'held' && p.strike !== null && p.optionTicker !== null) {
         return this.splitHeldPremiumRow(p);
      }
      return p;
   }

   /** The Protection twin of splitForPctOtm (see it). */
   private splitProtectionForPctOtm(p: PhaProtectionRow): PhaProtectionRow {
      if (p.origin === 'held' && p.strike !== null && p.optionTicker !== null) {
         return this.splitHeldProtectionRow(p);
      }
      return p;
   }

   /**
    * The Premium header's OTM mover (R15): a SHIFT, never a target. Every live Premium row with a
    * strike moves its own actual % OTM by `delta` (10.2 + 5 = 15.2, no rounding — owner ruling 3)
    * and re-selects the nearest listed strike; the hand-picked ones too. A strike change on a HELD
    * row trading a contract is an IDENTITY change (owner, 2026-10-01, smoke: the mover is a strike
    * re-pick — the held row is crossed out, a NEW row takes the shifted strike). Crossed-out rows
    * have no effect taken on them (owner, same smoke). Returns the rows whose strike changed.
    */
   @UniqueOptionEdit
   shiftPremiumPctOtm(delta: number): PhaPremiumRow[] {
      if (!isFinite(delta) || delta === 0) {
         return [];
      }
      const changed: PhaPremiumRow[] = [];
      const visited: {[id: string]: true} = {};
      // A snapshot: the walk SPLITS held rows, appending clones that must not be visited.
      this.premiumRows.slice().forEach(p => {
         if (p.strategyId) {
            if (visited[p.strategyId]) { return; }
            visited[p.strategyId] = true;
            const group = this.strategyRows('premium', p.id), anchor = group[0] as PhaPremiumRow;
            if (group.some(r => r.otmLock) || !anchor.expiration || anchor.pctOtm === null) { return; }
            const strike = resolveStrikeByType(anchor.optionType, anchor.expiration, this.spotOf(anchor.ticker), clampPctOtm(anchor.pctOtm + delta));
            if (strike !== null) { changed.push(...this.moveStrategy('premium', anchor.id, strike) as PhaPremiumRow[]); }
            return;
         }
         if (p.otmLock || p.crossedOut || !p.expiration || p.strike === null || p.pctOtm === null || this.isRowDisabled(p.id)) {
            return;
         }
         const resolved = resolveStrikeByType(p.optionType, p.expiration, this.spotOf(p.ticker), clampPctOtm(p.pctOtm + delta));
         if (resolved === null || resolved === p.strike) {
            return;                                    // the nearest listed strike does not move
         }
         const target = this.splitForPctOtm(p);
         if (target === p && p.origin === 'held') {
            return;                                    // a refused split: no effect on the row
         }
         this.clearPremiumQuote(target);
         target.strike = resolved;
         changed.push(target);
      });
      this.recompute();
      return changed;
   }

   /** An option quote arrived: every Premium row on that contract reprices. */
   setOptionQuote(optionTicker: string, bid: number | null, ask: number | null, feedMid: number | null = null,
                  feedLast: number | null = null): boolean {
      let touched = false;
      this.premiumRows.forEach(p => {
         if (p.optionTicker === optionTicker) {
            p.bid = bid;
            p.ask = ask;
            p.feedMid = feedMid;
            p.feedLast = feedLast;
            touched = true;
         }
      });
      if (touched) {
         this.recompute();
      }
      return touched;
   }

   /** Spot for a Premium ticker: the Underlying row's price when held, else the external spot (index rows). */
   spotOf(ticker: string): number | null {
      return this.spotOfSymbol(ticker);
   }

   /** By the row's own type (item 10 lets a PUT sit in Premium): see resolveStrikeByType. */
   private resolvePremiumStrike(p: PhaPremiumRow, pct: number): void {
      p.strike = resolveStrikeByType(p.optionType, p.expiration, this.spotOf(p.ticker), pct);
   }

   /** By the row's own type (a CALL may sit in Protection): see resolveStrikeByType. */
   private resolveProtectionStrike(p: PhaProtectionRow, pct: number): void {
      p.strike = resolveStrikeByType(p.optionType, p.expiration, this.spotOfSymbol(p.symbol), pct);
   }

   private clearPremiumOption(p: PhaPremiumRow): void {
      p.strike = null;
      this.clearPremiumQuote(p);
   }

   private clearPremiumQuote(p: PhaPremiumRow): void {
      p.optionTicker = null;
      p.bid = null;
      p.ask = null;
      p.feedMid = null;
      p.feedLast = null;
   }

   /** The Underlying rows changed shape or count: Premium rows follow (shares, available, cross-out, removal). */
   private syncPremiumToUnderlyings(): void {
      const ids = this.rows.map(r => r.id);
      // Index rows follow no underlying: they stay, whatever happens to the held names.
      this.premiumRows = this.premiumRows.filter(p => p.strategyType === 'Two-for-One' || this.isExpired(p) || !p.pooled || ids.indexOf(p.underlyingRowId) >= 0);
      this.premiumRows.forEach(p => {
         if (this.isExpired(p)) { p.crossedOut = false; p.contractsAvailable = 0; return; }
         if (!p.pooled) {
            p.crossedOut = p.removed;
            return;
         }
         const u = this.rows.find(r => r.id === p.underlyingRowId);
         if (!u && p.strategyType === 'Two-for-One') { p.sharesHeld = 0; p.crossedOut = p.removed; return; }
         p.ticker = underlyingSymbol(u);
         p.displayName = u.displayName;
         p.crossedOut = p.removed || u.crossedOut;
         p.sharesHeld = this.isExpired(u) || p.strategyType === 'Two-for-One' && (u.assetType !== 'Stock' || u.side !== 'Buy') ? 0 : backingShares(u);
      });
      // One pool per underlying (owner ruling 2026-09-29): walk the rows in order, each keeps at
      // most what is left after the rows before it, then sees the remainder as its own limit.
      const used: {[underlyingRowId: number]: number} = {};
      const visitedGroups: {[id: string]: true} = {};
      // Reserve all held/frozen trades first when a Two-for-One shares their backing.
      const coveredIds = new Set(this.premiumRows.filter(r => r.strategyType === 'Two-for-One').map(r => r.underlyingRowId));
      const priority = (p: PhaPremiumRow) => coveredIds.has(p.underlyingRowId) && this.strategyRows('premium', p.id)
         .some(r => this.isRowDisabled(r.id) || (r as PhaPremiumRow).heldContracts > 0) ? 0 : 1;
      const allocationRows = coveredIds.size ? this.premiumRows.slice().sort((a, b) => priority(a) - priority(b)) : this.premiumRows;
      allocationRows.forEach(p => {
         if (this.isExpired(p)) { p.crossedOut = false; p.contractsAvailable = 0; return; }
         if (!p.pooled) {
            p.contractsAvailable = 0;
            return;
         }
         const pool = Math.floor(p.sharesHeld / 100);
         const taken = used[p.underlyingRowId] || 0;
         if (p.crossedOut) {
            p.contractsAvailable = 0;
            return;
         }
         if (p.strategyId) {
            if (visitedGroups[p.strategyId]) { return; }
            visitedGroups[p.strategyId] = true;
            const group = this.strategyRows('premium', p.id) as PhaPremiumRow[];
            if (group.some(r => r.preserveInvalidBacking) && this.premiumUnits(this.premiumRows.filter(r =>
               r.underlyingRowId === p.underlyingRowId && !r.crossedOut && !this.isExpired(r))) <= pool) {
               group.forEach(r => r.preserveInvalidBacking = false);
            }
            if (p.strategyType === 'Two-for-One' && (group.some(r => r.preserveInvalidBacking) || !this.contractValidationEnabled)) {
               used[p.underlyingRowId] = taken + this.premiumUnits(group); return;
            }
            if (p.strategyType === 'Two-for-One' && (group.length !== 2 || group[0].strategyLeg !== 0 || group[1].strategyLeg !== 1
               || group[1].contractsSelling !== 2 * group[0].contractsSelling)) {
               used[p.underlyingRowId] = taken + this.premiumUnits(group); return;
            }
            let units = Math.max(...group.map(r => r.contractsSelling / strategyRatio(r)));
            const held = Math.max(...group.map(r => r.heldContracts / strategyRatio(r)));
            if (p.strategyType === 'Two-for-One' && held > 0) {
               // Explicit quantity reductions are allowed; automatic clamps cannot rewrite the book.
               units = Math.max(...group.map(r => r.contractsSelling / strategyRatio(r)));
               used[p.underlyingRowId] = taken + Math.max(units, held);
               return;
            }
            if (!group.some(r => this.isRowDisabled(r.id))) {
               units = Math.max(0, Math.min(units, Math.max(pool - taken, held)));
               group.filter(r => !r.crossedOut).forEach(r => r.contractsSelling = units * strategyRatio(r));
            }
            used[p.underlyingRowId] = taken + units;
            return;
         }
         // A session-locked row keeps its count whatever the pool does: its order is in the market.
         // A held row keeps at least what the book holds (the pool never manufactures a change).
         if (!this.isRowDisabled(p.id)) {
            p.contractsSelling = Math.max(0, Math.min(p.contractsSelling, Math.max(pool - taken, p.heldContracts)));
         }
         used[p.underlyingRowId] = taken + p.contractsSelling;
      });
      this.premiumRows.forEach(p => {
         if (this.isExpired(p) || p.crossedOut || !p.pooled) {
            return;
         }
         const pool = Math.floor(p.sharesHeld / 100);
         const others = (used[p.underlyingRowId] || 0) - p.contractsSelling / strategyRatio(p);
         p.contractsAvailable = Math.max(0, pool - others) * strategyRatio(p);
      });
   }

   private recomputePremium(): void {
      this.premiumRows.forEach(p => {
         p.pctOtm = pctOtmByType(p.optionType, p.strategyId && p.expectedStrike !== undefined ? p.expectedStrike : p.strike, this.spotOf(p.ticker));
         p.price = this.isExpired(p) ? this.settlementValue(p.optionTicker, p.optionType, p.strike)
            : optionPrice(p.bid, p.ask, p.feedMid, p.feedLast);
      });
      this.recomputeStrategyPrices(this.premiumRows);
      this.premiumTotals = optionAmounts(this.premiumRows, 'Premium', this.optionContributions || this.optionHoldings, r => this.isExpired(r), !!this.optionContributions, ticker => this.optionPointValue(ticker));
   }

   private recomputeStrategyPrices(rows: PhaOptionRow[]): void {
      const groups = new Map<string, PhaOptionRow[]>();
      rows.forEach(row => {
         row.netStrategyPrice = null;
         if (!row.strategyId) { return; }
         if (!groups.has(row.strategyId)) { groups.set(row.strategyId, []); }
         groups.get(row.strategyId).push(row);
      });
      groups.forEach(group => {
         const anchor = group.find(row => row.strategyLeg === 0);
         if (!anchor || !['Vertical', 'Butterfly', 'Two-for-One'].includes(anchor.strategyType)) { return; }
         const count = anchor.strategyType === 'Butterfly' ? 3 : 2;
         if (group.length !== count || new Set(group.map(row => row.strategyLeg)).size !== count
            || group.some(row => row.strategyType !== anchor.strategyType || !Number.isInteger(row.strategyLeg)
               || row.strategyLeg < 0 || row.strategyLeg >= count || !row.optionTicker
               || row.strike === null || !isFinite(row.strike) || row.price === null || !isFinite(row.price)
               || row.price < 0 || (row.side !== 'Buy' && row.side !== 'Sell'))) { return; }
         const net = group.reduce((sum, row) => sum + row.price * strategyRatio(row) * (row.side === 'Sell' ? 1 : -1), 0);
         // Round once after netting; normalise negative zero for neutral formatting/colour.
         anchor.netStrategyPrice = round2(net) || 0;
      });
   }

   private recomputeNetHedge(): void {
      const p = this.premiumTotals;
      const h = this.protectionTotals;
      this.netHedge = {
         premium: p.dollars, protection: h.dollars, net: round2(p.dollars + h.dollars),
         mixed: (p.hasHoldings || h.hasHoldings) && (p.hasTransactions || h.hasTransactions),
         premiumMixed: p.mixed, protectionMixed: h.mixed,
         premiumExisting: p.dollarsExisting, premiumTransaction: p.dollarsTransaction,
         protectionExisting: h.dollarsExisting, protectionTransaction: h.dollarsTransaction,
         netExisting: round2(p.dollarsExisting + h.dollarsExisting),
         netTransaction: round2(p.dollarsTransaction + h.dollarsTransaction)
      };
   }

   // ---- Protection (section 5 "Building the Protection section") ---------------------------------

   /** The instruments a Protection row may be on: the list (R2) plus every held name, no duplicates. */
   protectionChoices(): Array<{symbol: string, displayName: string}> {
      const out: Array<{symbol: string, displayName: string}> = PHA_PROTECTION_LIST.map(t => ({symbol: t, displayName: t}));
      this.rows.forEach(u => {
         if (!out.some(c => c.symbol === underlyingSymbol(u))) {
            out.push({symbol: underlyingSymbol(u), displayName: u.assetType === 'Call' ? underlyingSymbol(u) : u.displayName});
         }
      });
      return out;
   }

   /** A new row is a BUY PUT; a row loaded from a portfolio (item 10) brings its own type and side. */
   addProtectionRow(symbol: string, displayName?: string, optionType: PhaOptionType = 'Put', side: PhaSide = 'Buy',
                    origin: PhaRowOrigin = 'new'): PhaProtectionRow {
      const held = this.rows.find(r => underlyingSymbol(r) === symbol);
      const row: PhaProtectionRow = {
         id: ++this._seq,
         side,
         optionType,
         symbol,
         displayName: displayName || (held ? held.displayName : symbol),
         onHeldName: !!held,
         origin,
         removed: false,
         crossedOut: !!held && held.crossedOut,
         expirations: [],
         expiration: null,
         strike: null,
         pctOtm: null,
         otmLock: false,
         contracts: 0,
         optionTicker: null,
         bid: null,
         ask: null,
         feedMid: null,
         feedLast: null,
         price: null,
         netStrategyPrice: null,
         dollars: null,
         dollarsProtected: null
      };
      this.protectionRows.push(row);
      this.recompute();
      return row;
   }

   /** Remove: a HELD row is crossed out and stays, restorable; a NEW row is deleted (see removePremiumRow). */
   removeProtectionRow(id: number): 'deleted' | 'crossedOut' | 'none' {
      if (this.isRowDisabled(id)) { return 'none'; }
      const p = this.protectionRows.find(r => r.id === id);
      if (!p) {
         return 'none';
      }
      if (p.origin === 'held') {
         p.removed = true;
         this.recompute();
         return 'crossedOut';
      }
      this.protectionRows = this.protectionRows.filter(r => r.id !== id);
      this.recompute();
      return 'deleted';
   }

   /** Restore a crossed-out held Protection row (its own cross-out only; the held name's still applies). */
   @UniqueOptionEdit
   restoreProtectionRow(id: number): void {
      if (this.isRowDisabled(id)) { return; }
      const p = this.protectionRows.find(r => r.id === id);
      if (p && p.removed) {
         p.removed = false;
         this.recompute();
      }
   }

   /**
    * The row's SIDE picked (item 4): the sign of its dollars follows (BUY = debit, SELL = credit).
    * On a HELD row a side change REVERSES the position (owner, 2026-10-01 vrequest, the Hedge Pad
    * rule): the held row is crossed out — the close — and a NEW row with the opposite side opens.
    * A NEW row edits in place; a session-locked or crossed-out held row refuses.
    */
   @UniqueOptionEdit
   setProtectionSide(id: number, side: PhaSide): void {
      if (this.protectionRows.some(r => r.id === id && r.strategyId)) { this.reverseStrategy('protection', id, side); return; }
      const p = this.protectionRows.find(r => r.id === id);
      if (!p || PHA_SIDES.indexOf(side) < 0 || side === p.side || this.isRowDisabled(id)) {
         return;
      }
      if (p.origin === 'held') {
         const clone = this.splitHeldProtectionRow(p);
         if (clone === p) {
            return;
         }
         clone.side = side;
      } else {
         p.side = side;
      }
      this.recompute();
   }

   /** The Protection twin of splitHeldPremiumRow (see it for the rule). */
   private splitHeldProtectionRow(p: PhaProtectionRow): PhaProtectionRow {
      if (p.origin !== 'held' || p.removed || p.crossedOut || this.isRowDisabled(p.id)) {
         return p;
      }
      p.removed = true;
      // The clone keeps everything, the quote fields included: see splitHeldPremiumRow.
      const clone: PhaProtectionRow = Object.assign({}, p, {
         id: ++this._seq,
         origin: 'new' as PhaRowOrigin,
         removed: false,
         crossedOut: false,
         dollars: null,
         dollarsProtected: null
      });
      this.protectionRows.push(clone);
      return clone;
   }

   @UniqueOptionEdit
   setProtectionSymbol(id: number, symbol: string, displayName?: string): PhaProtectionRow {
      const p = this.protectionRows.find(r => r.id === id);
      if (!p) {
         return null;
      }
      if (p.origin === 'held' || this.isRowDisabled(id)) {
         return p;                         // owner, 2026-10-01: a held row's instrument cannot change
      }
      const held = this.rows.find(r => underlyingSymbol(r) === symbol);
      p.symbol = symbol;
      p.displayName = displayName || (held ? held.displayName : symbol);
      p.onHeldName = !!held;
      p.expirations = [];
      p.expiration = null;
      this.clearProtectionOption(p);
      this.recompute();
      return p;
   }

   setProtectionExpirations(symbol: string, expirations: PhaExpiration[]): void {
      this.protectionRows.filter(p => p.symbol === symbol).forEach(p => p.expirations = expirations || []);
   }

   /**
    * Expiration selected: the strike resolves at once to the default % OTM BELOW spot (owner ruling
    * 2026-09-29, R15 — mirrors Premium; before R15 the strike waited for a hand pick). On a HELD
    * row the pick is an identity change (owner, 2026-10-01 vrequest): the held row is crossed out
    * and a NEW row takes the expiration, its strike resolved at the HELD row's % OTM (see
    * setPremiumExpiration). Returns the row (the new one when split).
    */
   @UniqueOptionEdit
   setProtectionExpiration(id: number, expiration: PhaExpiration | null): PhaProtectionRow {
      const linked = this.protectionRows.find(r => r.id === id && r.strategyId);
      if (linked) { return this.setStrategyExpiration('protection', id, expiration).find(r => r.strategyLeg === linked.strategyLeg) as PhaProtectionRow || linked; }
      const p = this.protectionRows.find(r => r.id === id);
      if (!p) {
         return null;
      }
      if (this.isRowDisabled(id)) {
         return p;
      }
      if (p.expiration === expiration || (p.expiration && expiration && p.expiration.date === expiration.date)) {
         return p;                                     // the same expiration picked again
      }
      // A RE-PICK on a HELD row is an identity change (owner, 2026-10-01 vrequest); the initial
      // fill from null (the import, the state restore) is not. See setPremiumExpiration.
      let target = p;
      if (p.origin === 'held' && p.expiration !== null) {
         target = this.splitHeldProtectionRow(p);
         if (target === p) {
            return p;                                  // session-locked / crossed out: refused
         }
      }
      target.expiration = expiration;
      this.clearProtectionOption(target);
      if (expiration) {
         this.resolveProtectionStrike(target, target.pctOtm !== null ? target.pctOtm : PHA_DEFAULT_PROTECTION_PCT_OTM);
      }
      this.recompute();
      return target;
   }

   /**
    * The row's % OTM typed or spun (R15): see setPremiumPctOtm; here % below spot, negative = above.
    * On a HELD row trading a contract a strike change is an IDENTITY change (owner, 2026-10-01):
    * the held row is crossed out, a NEW row takes the resolved strike.
    */
   @UniqueOptionEdit
   setProtectionPctOtm(id: number, pct: number): PhaProtectionRow {
      const p = this.protectionRows.find(r => r.id === id);
      if (!p || !p.expiration || !isFinite(pct) || this.isRowDisabled(id)) {
         return p || null;
      }
      const resolved = resolveStrikeByType(p.optionType, p.expiration, this.spotOfSymbol(p.symbol), clampPctOtm(pct));
      if (p.strategyId) {
         return resolved === null ? p : this.moveStrategy('protection', id, resolved).find(r => r.strategyLeg === p.strategyLeg) as PhaProtectionRow || p;
      }
      if (resolved === null || resolved === p.strike) {
         return p;                                     // the same strike: no contract change, no split
      }
      const target = this.splitProtectionForPctOtm(p);
      if (target === p && p.origin === 'held') {
         return p;                                     // a crossed-out / locked held row: no effect
      }
      this.clearProtectionQuote(target);
      target.strike = resolved;
      this.recompute();
      return target;
   }

   /** The row's OTM lock (R18): a locked row ignores the header's OTM mover; its own % OTM box still works. */
   togglePremiumOtmLock(id: number): void {
      if (this.isRowDisabled(id)) { return; }
      const p = this.premiumRows.find(r => r.id === id);
      if (p) {
         p.otmLock = !p.otmLock;
      }
   }

   toggleProtectionOtmLock(id: number): void {
      if (this.isRowDisabled(id)) { return; }
      const p = this.protectionRows.find(r => r.id === id);
      if (p) {
         p.otmLock = !p.otmLock;
      }
   }

   /**
    * The Protection header's OTM mover (R15): see shiftPremiumPctOtm; acts on Protection rows only.
    * A HELD row trading a contract SPLITS on a strike change; crossed-out rows are untouched.
    */
   @UniqueOptionEdit
   shiftProtectionPctOtm(delta: number): PhaProtectionRow[] {
      if (!isFinite(delta) || delta === 0) {
         return [];
      }
      const changed: PhaProtectionRow[] = [];
      const visited: {[id: string]: true} = {};
      // A snapshot: the walk SPLITS held rows, appending clones that must not be visited.
      this.protectionRows.slice().forEach(p => {
         if (p.strategyId) {
            if (visited[p.strategyId]) { return; }
            visited[p.strategyId] = true;
            const group = this.strategyRows('protection', p.id), anchor = group[0] as PhaProtectionRow;
            if (group.some(r => r.otmLock) || !anchor.expiration || anchor.pctOtm === null) { return; }
            const strike = resolveStrikeByType(anchor.optionType, anchor.expiration, this.spotOfSymbol(anchor.symbol), clampPctOtm(anchor.pctOtm + delta));
            if (strike !== null) { changed.push(...this.moveStrategy('protection', anchor.id, strike) as PhaProtectionRow[]); }
            return;
         }
         if (p.otmLock || p.crossedOut || !p.expiration || p.strike === null || p.pctOtm === null || this.isRowDisabled(p.id)) {
            return;
         }
         const resolved = resolveStrikeByType(p.optionType, p.expiration, this.spotOfSymbol(p.symbol), clampPctOtm(p.pctOtm + delta));
         if (resolved === null || resolved === p.strike) {
            return;                                    // the nearest listed strike does not move
         }
         const target = this.splitProtectionForPctOtm(p);
         if (target === p && p.origin === 'held') {
            return;                                    // a refused split: no effect on the row
         }
         this.clearProtectionQuote(target);
         target.strike = resolved;
         changed.push(target);
      });
      this.recompute();
      return changed;
   }

   /** On a HELD row an identity change (owner, 2026-10-01 vrequest): see setPremiumStrike. Returns the row (the new one when split). */
   @UniqueOptionEdit
   setProtectionStrike(id: number, strike: number | null): PhaProtectionRow {
      const linked = this.protectionRows.find(r => r.id === id && r.strategyId);
      if (linked) { return strike === null ? linked : this.moveStrategy('protection', id, strike).find(r => r.strategyLeg === linked.strategyLeg) as PhaProtectionRow || linked; }
      const p = this.protectionRows.find(r => r.id === id);
      if (!p) {
         return null;
      }
      if (this.isRowDisabled(id)) {
         return p;
      }
      if (p.strike === strike) {
         return p;                                     // the same strike picked again
      }
      // A RE-PICK on a HELD row that already trades a contract (ticker set) is an identity change
      // (owner, 2026-10-01 vrequest); the initial fill from null is not. See setPremiumStrike.
      let target = p;
      if (p.origin === 'held' && p.strike !== null && p.optionTicker !== null) {
         target = this.splitHeldProtectionRow(p);
         if (target === p) {
            return p;                                  // session-locked / crossed out: refused
         }
      }
      this.clearProtectionOption(target);
      target.strike = strike;
      this.recompute();
      return target;
   }

   @UniqueOptionEdit
   setProtectionOptionTicker(id: number, optionTicker: string | null): void {
      const p = this.protectionRows.find(r => r.id === id);
      if (p) {
         p.optionTicker = optionTicker;
      }
   }

   setProtectionContracts(id: number, contracts: number): void {
      if (this.protectionRows.some(r => r.id === id && r.strategyId)) { this.setStrategyContracts('protection', id, contracts); return; }
      if (this.isRowDisabled(id)) { return; }
      const p = this.protectionRows.find(r => r.id === id);
      if (!p || p.crossedOut) {
         return;
      }
      const n = Math.floor(isFinite(contracts) ? contracts : 0);
      p.contracts = Math.max(0, n);
      this.recompute();
   }

   /** True when a Protection row or an index Premium row sits on a symbol that is not held (its spot comes from the feed). */
   needsExternalSpot(symbol: string): boolean {
      return !!symbol && !this.hasTicker(symbol)
         && (this.rows.some(r => r.callUnderlying === symbol) || this.protectionRows.some(p => p.symbol === symbol) || this.premiumRows.some(p => !p.pooled && p.ticker === symbol));
   }

   /** Spot for an instrument that is not held — a Protection instrument or an index Premium row (fed by the panel's quote stream). */
   setProtectionSpot(symbol: string, price: number | null): boolean {
      if (!this.rows.some(r => r.callUnderlying === symbol) && !this.protectionRows.some(p => p.symbol === symbol) && !this.premiumRows.some(p => !p.pooled && p.ticker === symbol)) {
         return false;
      }
      if (price === null) {
         delete this.protectionSpots[symbol];
      } else {
         this.protectionSpots[symbol] = price;
      }
      this.recompute();
      return true;
   }

   /** An option quote arrived for a protection contract. */
   setProtectionQuote(optionTicker: string, bid: number | null, ask: number | null, feedMid: number | null = null,
                      feedLast: number | null = null): boolean {
      let touched = false;
      this.protectionRows.forEach(p => {
         if (p.optionTicker === optionTicker) {
            p.bid = bid;
            p.ask = ask;
            p.feedMid = feedMid;
            p.feedLast = feedLast;
            touched = true;
         }
      });
      if (touched) {
         this.recompute();
      }
      return touched;
   }

   /** Spot for any symbol: the Underlying row's price when held, else the protection spot. */
   spotOfSymbol(symbol: string): number | null {
      const held = this.rows.find(r => (r.assetType === 'Stock' || r.assetType === 'Future') && r.ticker === symbol);
      if (held && held.price !== null) {
         return held.price;
      }
      const s = this.protectionSpots[symbol];
      return typeof s === 'number' && isFinite(s) ? s : null;
   }

   private clearProtectionOption(p: PhaProtectionRow): void {
      p.strike = null;
      this.clearProtectionQuote(p);
   }

   private clearProtectionQuote(p: PhaProtectionRow): void {
      p.optionTicker = null;
      p.bid = null;
      p.ask = null;
      p.feedMid = null;
      p.feedLast = null;
   }

   /**
    * A protection row on a held name follows it ([my call]): deleted with it, crossed out with it.
    * A row on one of the list's instruments follows nothing.
    */
   private syncProtectionToUnderlyings(): void {
      this.protectionRows = this.protectionRows.filter(p => this.isExpired(p) || !p.onHeldName || this.rows.some(u => underlyingSymbol(u) === p.symbol));
      this.protectionRows.forEach(p => {
         if (this.isExpired(p)) { p.crossedOut = false; return; }
         const held = this.rows.filter(u => underlyingSymbol(u) === p.symbol);
         p.crossedOut = p.removed || (p.onHeldName && held.length > 0 && held.every(u => u.crossedOut));
      });
   }

   private recomputeProtection(): void {
      this.protectionRows.forEach(p => {
         p.pctOtm = pctOtmByType(p.optionType, p.strategyId && p.expectedStrike !== undefined ? p.expectedStrike : p.strike, this.spotOfSymbol(p.symbol));
         p.price = this.isExpired(p) ? this.settlementValue(p.optionTicker, p.optionType, p.strike)
            : optionPrice(p.bid, p.ask, p.feedMid, p.feedLast);
      });
      this.recomputeStrategyPrices(this.protectionRows);
      this.protectionTotals = optionAmounts(this.protectionRows, 'Protection', this.optionContributions || this.optionHoldings, r => this.isExpired(r), !!this.optionContributions, ticker => this.optionPointValue(ticker));
      this.protectionRows.forEach(p => p.dollarsProtected = p.hasHolding ? p.protectedExisting : p.protectedTransaction);
   }

   // ---- the balance moves (section 5 "The balance buttons") --------------------------------------
   //
   // Allocation defines the budget for Auto-Balance and Beta Optimization:
   // account value (portfolio plus cash) x allocation percent. Existing quantities and added
   // stocks do not change that budget. Raising/lowering allocation changes the next balance.
   // Every move is all-or-nothing: the new share counts are
   // computed on the side, and only when every unlocked row has one are they written. A
   // shares-locked row keeps its count and its dollars (shares x price) come off the top; a
   // %-locked row keeps its % and its shares are recomputed to hold that % of the move's budget.
   // What is left is shared out among unlocked rows. Crossed-out rows
   // take no part. A press clears every pending % request (the red goes).
   //
   // Shares are WHOLE numbers (owner ruling 2026-09-29, replacing the "unrounded" line of the
   // system description): every computed count is floored, never rounded up, so a move never
   // spends more than the move's budget. The floor is applied once, at the commit, so the split
   // functions stay pure arithmetic.

   balanceByPercent(): PhaBalanceResult {
      return this.balance('percent', (rows, remaining) => {
         const weight = remaining / rows.length;
         return rows.map(r => weight / this.unitPrice(r));
      });
   }

   /**
    * Equal beta-delta: shares x beta is the same K for every unlocked row, scaled so the dollars
 * fit the remaining stock allocation: K x sum(price/beta) = remaining. Chris's check: beta 1 at
    * 100 shares means beta 0.5 at 200 shares. Refused, naming the row, when an unlocked row has
    * no beta (or a non-positive one, which has no meaning here).
    */
   balanceByBeta(): PhaBalanceResult {
      const bad = this.unlockedRows().find(r => r.beta === null || !(r.beta > 0));
      if (bad) {
         return {
            applied: false,
            reason: bad.displayName + ' has no beta; beta optimization needs a beta on every unlocked row',
            rowTicker: bad.ticker
         };
      }
      return this.balance('beta', (rows, remaining) => {
         const sumPriceOverBeta = rows.reduce((sum, r) => sum + this.unitPrice(r) / this.unitBeta(r), 0);
         const k = remaining / sumPriceOverBeta;
         return rows.map(r => k / this.unitBeta(r));
      });
   }

   captureOptimization(): PhaOptimizationSnapshot {
      return {rows: this.rows.map(r => ({id: r.id, shares: r.shares, pctOverride: r.pctOverride})),
         premium: this.premiumRows.map(r => ({id: r.id, contractsSelling: r.contractsSelling,
            preserveInvalidBacking: r.preserveInvalidBacking})),
         protection: this.protectionRows.map(r => ({id: r.id, contracts: r.contracts})), lastBalance: this.lastBalance};
   }

   private optimizationTrial(): PhaSheet {
      const trial = Object.create(Object.getPrototypeOf(this)) as PhaSheet;
      Object.keys(this).forEach(key => {
         const value = this[key];
         trial[key] = Array.isArray(value) ? value.map(item => item && typeof item === 'object' ? {...item} : item)
            : value && typeof value === 'object' ? {...value} : value;
      });
      return trial;
   }

   /** Current contracts, held commitments and grouped ratios share the owning model's pool arithmetic. */
   roundingInput(): PhaRoundingInput {
      const excluded: string[] = [];
      const stocks = this.countingRows.filter(r => {
         if (r.assetType === 'Stock' && r.side === 'Buy' && r.shares > 0) { return true; }
         excluded.push(r.displayName + ': not an active long stock holding'); return false;
      }).map(r => {
         const commitments = this.premiumRows.filter(p => p.underlyingRowId === r.id
            && !this.isExpired(p) && (!p.crossedOut || p.heldContracts > 0));
         const units = this.premiumUnits(commitments.map(p => ({...p,
            contractsSelling: Math.max(p.crossedOut ? 0 : p.contractsSelling, p.heldContracts || 0)})));
         return {id: r.id, symbol: r.ticker, shares: r.shares, price: r.price === null || this.pointValue(r) === null ? null : this.unitPrice(r),
            fixed: r.sharesLock || r.pctLock || this.isRowDisabled(r.id), minimumShares: Math.ceil(units) * 100};
      });
      return {stocks, deviationPercent: this.roundingDeviationPercent, excluded};
   }

   /** Validate with latest marks, then recompute on a trial before touching any live quantity. */
   applyRounding(report: PhaRoundingReport): PhaBalanceResult {
      if (!this.isUnlocked || report.deviationPercent !== this.roundingDeviationPercent) {
         return {applied: false, reason: 'The analysis settings changed during calculation.'};
      }
      const input = this.roundingInput();
      if (input.stocks.length !== report.lines.length || input.stocks.some(s => {
         const line = report.lines.find(l => l.id === s.id);
         return !line || line.symbol !== s.symbol || line.shares !== s.shares || !Number.isFinite(s.price) || !(s.price > 0)
            || !Number.isInteger(line.afterShares) || line.afterShares < s.minimumShares
            || s.fixed && line.afterShares !== s.shares;
      })) { return {applied: false, reason: 'Holdings, locks, prices or option backing changed during calculation.'}; }
      const before = input.stocks.reduce((v, s) => v + s.shares * s.price, 0);
      const after = input.stocks.reduce((v, s) => v + report.lines.find(l => l.id === s.id).afterShares * s.price, 0);
      const coveredBefore = input.stocks.reduce((v, s) => v + Math.floor(s.shares / 100) * 100 * s.price, 0);
      const coveredAfter = input.stocks.reduce((v, s) => v + Math.floor(report.lines.find(l => l.id === s.id).afterShares / 100) * 100 * s.price, 0);
      if (after > before + 1e-7 || coveredAfter <= coveredBefore + 1e-7 || input.stocks.some(s =>
         Math.abs(report.lines.find(l => l.id === s.id).afterShares * before / (s.shares * after) - 1)
            > input.deviationPercent / 100 + 1e-10)) {
         return {applied: false, reason: 'Current prices no longer satisfy the funding, deviation or improvement limits. Please calculate again.'};
      }
      const change = (target: PhaSheet) => {
         report.lines.forEach(l => {
            const row = target.rows.find(r => r.id === l.id); row.shares = l.afterShares;
            if (!row.pctLock && !row.sharesLock && !target.isRowDisabled(row.id)) { row.pctOverride = null; }
         });
         target.lastBalance = 'rounding'; target.recompute();
      };
      const trial = this.optimizationTrial(); change(trial);
      const original = this.captureOptimization(), proposed = trial.captureOptimization();
      if (JSON.stringify(original.premium) !== JSON.stringify(proposed.premium)
         || JSON.stringify(original.protection) !== JSON.stringify(proposed.protection)
         || trial.twoForOneBackingProblems().length > this.twoForOneBackingProblems().length) {
         return {applied: false, reason: 'The proposal would change option quantities or their required stock backing.'};
      }
      change(this); return {applied: true};
   }

   /** Undo editable optimization quantities only; current quotes and accounting references stay live. */
   restoreOptimization(snapshot: PhaOptimizationSnapshot): PhaBalanceResult {
      if (snapshot.rows.length !== this.rows.length || snapshot.premium.length !== this.premiumRows.length
         || snapshot.protection.length !== this.protectionRows.length
         || snapshot.rows.some(s => !this.rows.some(r => r.id === s.id)
            || this.isRowDisabled(s.id) && this.rows.find(r => r.id === s.id).shares !== s.shares)
         || snapshot.premium.some(s => !this.premiumRows.some(r => r.id === s.id)
            || this.isRowDisabled(s.id) && this.premiumRows.find(r => r.id === s.id).contractsSelling !== s.contractsSelling)
         || snapshot.protection.some(s => !this.protectionRows.some(r => r.id === s.id)
            || this.isRowDisabled(s.id) && this.protectionRows.find(r => r.id === s.id).contracts !== s.contracts)) {
         return {applied: false, reason: 'The analysis or exported orders changed; this optimization cannot be undone.'};
      }
      const restore = (target: PhaSheet) => {
         snapshot.rows.forEach(s => Object.assign(target.rows.find(r => r.id === s.id), {shares: s.shares, pctOverride: s.pctOverride}));
         snapshot.premium.forEach(s => Object.assign(target.premiumRows.find(r => r.id === s.id),
            {contractsSelling: s.contractsSelling, preserveInvalidBacking: s.preserveInvalidBacking}));
         snapshot.protection.forEach(s => target.protectionRows.find(r => r.id === s.id).contracts = s.contracts);
         target.lastBalance = snapshot.lastBalance; target.recompute();
      };
      const trial = this.optimizationTrial(); restore(trial);
      if (JSON.stringify(trial.captureOptimization()) !== JSON.stringify(snapshot)) {
         return {applied: false, reason: 'Current option backing prevents restoring the original quantities.'};
      }
      restore(this); return {applied: true};
   }

   private unlockedRows(): PhaUnderlyingRow[] {
      return this.countingRows.filter(r => !r.sharesLock && !r.pctLock && !this.isRowDisabled(r.id));
   }

   @UniqueOptionEdit
   private balance(mode: PhaBalanceMode,
                   split: (rows: PhaUnderlyingRow[], remaining: number) => number[]): PhaBalanceResult {
      if (!this.isUnlocked) {
         return {applied: false, reason: 'Enter an account value first'};
      }
      const counting = this.countingRows;
      if (counting.length === 0) {
         return {applied: false, reason: 'There are no underlyings to balance'};
      }
      const unpriced = counting.find(r => !this.isExpired(r) && (r.price === null || !(r.price > 0) || this.pointValue(r) === null));
      if (unpriced) {
         return {applied: false, reason: unpriced.displayName + (this.pointValue(unpriced) === null ? ' has no valid point value yet' : ' has no price yet'), rowTicker: unpriced.ticker};
      }

      // Off the top: the locked rows.
      const budget = this.maxStockValue || 0;
      let remaining = budget;
      const pctLockedShares: {[id: number]: number} = {};
      counting.forEach(r => {
         // A session-locked row (its order is copied to ToS) keeps its shares: off the top, as a shares lock.
         if (r.sharesLock || this.isRowDisabled(r.id)) {
            remaining -= r.shares * this.unitPrice(r);
         } else if (r.pctLock) {
            const pct = r.pctOverride !== null ? r.pctOverride : (r.pct !== null ? r.pct : 0);
            const dollars = budget * pct / 100;
            remaining -= dollars;
            pctLockedShares[r.id] = dollars / this.unitPrice(r);
         }
      });
      if (remaining < 0) {
         return {applied: false, reason: 'The locked rows alone exceed the balance budget'};
      }

      const unlocked = this.unlockedRows();
      const shares = unlocked.length ? split(unlocked, remaining) : [];
      if (shares.some(v => !isFinite(v) || v < 0)) {
         return {applied: false, reason: 'The balance could not be computed'};
      }

      // Commit: every unlocked row at once, the %-locked rows to their held %, no request left.
      // Whole shares only: floor, never round up (see the section comment above).
      unlocked.forEach((r, i) => r.shares = Math.floor(shares[i]));
      counting.forEach(r => {
         if (this.isRowDisabled(r.id)) {
            return; // frozen: shares and any % request stay as they are
         }
         if (r.pctLock) {
            r.shares = Math.floor(pctLockedShares[r.id]);
            // The held % stays exactly as the trader set it: keep it as the row's figure.
            r.pctOverride = r.pctOverride !== null ? r.pctOverride : r.pct;
         } else {
            r.pctOverride = null;
         }
      });
      this.lastBalance = mode;
      this.recompute();
      return {applied: true};
   }

   /**
    * The consistency group (§4): shares, price, dollars, % and beta-Δ of every row plus the totals
    * recompute as ONE unit. Crossed-out rows are excluded everywhere.
    */
   recompute(): void {
      this.syncPremiumToUnderlyings();
      this.syncProtectionToUnderlyings();
      const counting = this.countingRows;

      this.rows.forEach(r => {
         if (this.isExpired(r)) {
            r.price = this.settlementValue(r.ticker, 'Call', r.callStrike);
            r.priceStale = false;
         }
         r.dollars = r.price === null || this.pointValue(r) === null ? null : round2(r.shares * this.unitPrice(r));
         // Whole market-equivalent shares (owner, 2026-09-29 vrequest, item 7): .5 and up rounds up.
         r.betaDelta = this.isExpired(r) ? 0 : r.beta === null || this.pointValue(r) === null ? null : roundHalfUp(r.shares * this.unitBeta(r));
      });

      const missingValue = counting.some(r => r.dollars === null && r.shares > 0);
      const missingPointValue = counting.some(r => r.shares > 0 && this.pointValue(r) === null);
      const totalDollars = counting.reduce((sum, r) => sum + (r.dollars || 0), 0);

      this.rows.forEach(r => {
         if (r.crossedOut) {
            r.pct = null;
            return;
         }
         if (r.pctOverride !== null) {
            r.pct = r.pctOverride;
         } else if (missingValue || r.dollars === null || totalDollars <= 0) {
            r.pct = null;
         } else {
            r.pct = round2(r.dollars / totalDollars * 100);
         }
      });

      const totalPct = counting.reduce((sum, r) => sum + (r.pct || 0), 0);
      const anyBeta = counting.some(r => r.betaDelta !== null);
      const totalBetaDelta = anyBeta ? counting.reduce((sum, r) => sum + (r.betaDelta || 0), 0) : null;

      // Row percentages describe the mix of the stock portfolio, not its share of account value.
      const consistent = !missingPointValue && totalPct <= 100 + PCT_TOLERANCE + 0.01 * counting.length;
      const maxStockValue = this.maxStockValue;
      const dollarsConsistent = !missingPointValue && (maxStockValue === null || round2(totalDollars) <= maxStockValue + 0.005);

      this.recomputeAccountValuation();
      const freeCash = this.currentAccountValue === null ? null : round2(this._accountCash);

      this.totals = {
         dollars: missingValue ? null : round2(totalDollars),
         pct: round2(totalPct),
         betaDelta: totalBetaDelta === null ? null : roundHalfUp(totalBetaDelta),
         freeCash,
         pctConsistent: consistent,
         dollarsConsistent
      };

      this.recomputePremium();
      this.recomputeProtection();
      this.recomputeNetHedge();
   }
}

/** The price/chain symbol is separate from a long call's contract identity. */
export function underlyingSymbol(row: PhaUnderlyingRow): string { return row.callUnderlying || row.ticker; }
/** One long call contract represents 100 shares for the existing Premium pool (delta placeholder 1). */
export function backingShares(row: PhaUnderlyingRow): number { return row.shares * (row.assetType === 'Call' ? 100 : 1); }

/**
 * The listed strike nearest a target ABOVE spot; a tie between two equally near strikes resolves
 * away from spot (further OTM) — [my call], spec section 2. Strikes at or below spot are never
 * picked while a strike above spot exists.
 */
export function nearestStrikeAbove(strikes: number[], target: number, spot: number): number | null {
   if (!strikes || !strikes.length) {
      return null;
   }
   const above = strikes.filter(k => k > spot);
   const pool = above.length ? above : strikes;
   let best: number = null;
   let bestDist = Infinity;
   pool.forEach(k => {
      const d = Math.abs(k - target);
      if (d < bestDist - 1e-9 || (Math.abs(d - bestDist) <= 1e-9 && best !== null && k > best)) {
         best = k;
         bestDist = d;
      }
   });
   return best;
}

/**
 * The mirror for puts: the listed strike nearest a target BELOW spot; a tie resolves away from spot
 * (further OTM = lower). Strikes at or above spot are never picked while a strike below spot exists.
 */
export function nearestStrikeBelow(strikes: number[], target: number, spot: number): number | null {
   if (!strikes || !strikes.length) {
      return null;
   }
   const below = strikes.filter(k => k < spot);
   const pool = below.length ? below : strikes;
   let best: number = null;
   let bestDist = Infinity;
   pool.forEach(k => {
      const d = Math.abs(k - target);
      if (d < bestDist - 1e-9 || (Math.abs(d - bestDist) <= 1e-9 && best !== null && k < best)) {
         best = k;
         bestDist = d;
      }
   });
   return best;
}

/** A row's % OTM lives in [-100, +100] (owner, 2026-09-29): negative = in the money. */
export function clampPctOtm(pct: number): number {
   return Math.max(PHA_MIN_PCT_OTM, Math.min(PHA_MAX_PCT_OTM, pct));
}

/**
 * The platform's option price rule: the two-sided mid when both sides are quoted, else the feed's
 * own mid, else its last (the backend's Mid == 0 ? Last : Mid). Null = unpriced.
 */
export function optionPrice(bid: number | null, ask: number | null, feedMid: number | null, feedLast: number | null): number | null {
   if (bid !== null && ask !== null && bid >= 0 && ask > 0) {
      return Math.round((bid + ask) / 2 * 10000) / 10000;
   }
   if (feedMid !== null && isFinite(feedMid) && feedMid > 0) {
      return feedMid;
   }
   if (feedLast !== null && isFinite(feedLast) && feedLast > 0) {
      return feedLast;
   }
   return null;
}

function round2(v: number): number {
   return Math.round(v * 100) / 100;
}

/** The owner's rounding rule (item 7): .5 and up goes to the ceiling, below .5 to the floor. */
export function roundHalfUp(v: number): number {
   return Math.floor(v + 0.5);
}

/**
 * The listed strike nearest a target at `pct` % out of the money for the row's TYPE (item 10 lets a
 * PUT sit in Premium and a CALL in Protection, so the direction follows the type, not the section):
 * a CALL is OTM above spot, a PUT below; a negative % is in the money, on the other side of spot.
 */
export function resolveStrikeByType(type: PhaOptionType, expiration: PhaExpiration | null, spot: number | null, pct: number): number | null {
   if (!expiration || spot === null || !(spot > 0) || !expiration.strikes || !expiration.strikes.length) {
      return null;
   }
   const isCall = type === 'Call';
   // ATM uses the nearest listed strike on either side, including an exact spot strike.
   // The OTM helpers deliberately exclude ATM and therefore cannot resolve a zero offset.
   if (pct === 0) {
      return expiration.strikes.reduce((best, strike) => {
         const distance = Math.abs(strike - spot), previous = Math.abs(best - spot);
         return distance < previous - 1e-9 || Math.abs(distance - previous) <= 1e-9 && (isCall ? strike > best : strike < best)
            ? strike : best;
      }, expiration.strikes[0]);
   }
   const target = spot * (1 + (isCall ? pct : -pct) / 100);
   const above = (isCall && pct >= 0) || (!isCall && pct < 0);
   return above ? nearestStrikeAbove(expiration.strikes, target, spot) : nearestStrikeBelow(expiration.strikes, target, spot);
}

/** How far out of the money a strike sits for the row's TYPE, in % of spot (positive = OTM, negative = ITM). */
export function pctOtmByType(type: PhaOptionType, strike: number | null, spot: number | null): number | null {
   if (strike === null || spot === null || !(spot > 0)) {
      return null;
   }
   return round2((type === 'Call' ? (strike / spot - 1) : (1 - strike / spot)) * 100);
}

/** Derive holding valuation and trade cash flow without changing the editable row quantities.
 * Contracts are netted exactly once, as in the order builder. A duplicate row never duplicates
 * the book position. Held rows receive their baseline first; newly acquired rows then receive it.
 */
function optionAmounts(rows: Array<PhaPremiumRow | PhaProtectionRow>, section: string,
                       holdings: PhaOptionHoldings | null, expired: (row: PhaPremiumRow | PhaProtectionRow) => boolean, byStrategy = false, pointValue: (ticker: string) => number = () => 100): PhaProtectionTotals & PhaPremiumTotals {
   const groups: {[key: string]: Array<PhaPremiumRow | PhaProtectionRow>} = {};
   const quantity = (r: PhaPremiumRow | PhaProtectionRow) =>
      'contractsSelling' in r ? r.contractsSelling : r.contracts;
   const signed = (r: PhaPremiumRow | PhaProtectionRow) => (r.side === 'Buy' ? 1 : -1);
   rows.forEach(r => {
      const key = (r.optionTicker || ('unselected:' + r.id)) + (byStrategy && r.strategyId ? '|strategy:' + r.strategyId : '');
      (groups[key] || (groups[key] = [])).push(r);
   });
   Object.keys(groups).forEach(code => {
      const group = groups[code];
      // The fallback supports a sheet before its session has been attached.
      let book = holdings !== null ? (holdings[byStrategy
         ? optionContributionKey(section, group[0].optionTicker, group[0].strategyId) : section + '|' + code] || 0)
         : group.reduce((n, r) => n + (r.origin === 'held'
            ? signed(r) * ('heldContracts' in r ? r.heldContracts : quantity(r)) : 0), 0);
      const before: {[id: number]: number} = {};
      const eligible = group.filter(r => signed(r) * book > 0)
         .sort((a, b) => Number(b.origin === 'held') - Number(a.origin === 'held'));
      eligible.forEach((r, i) => {
         const capacity = Math.max(quantity(r), 'heldContracts' in r ? r.heldContracts : 0);
         const amount = i === eligible.length - 1 ? Math.abs(book) : Math.min(Math.abs(book), capacity);
         before[r.id] = Math.sign(book) * amount;
         book -= before[r.id];
      });
      const after = (r: PhaPremiumRow | PhaProtectionRow) => r.crossedOut ? 0 : signed(r) * quantity(r);
      let trade = group.reduce((n, r) => n + after(r) - (before[r.id] || 0), 0);
      group.forEach(r => {
         const held = before[r.id] || 0;
         const desired = after(r);
         const delta = desired - held;
         // Opposing duplicate rows cancel before display, just as they do in the exported order.
         const transaction = !expired(r) && delta * trade > 0 ? Math.sign(trade) * Math.min(Math.abs(delta), Math.abs(trade)) : 0;
         trade -= transaction;
         r.hasHolding = held !== 0;
         r.hasTransaction = transaction !== 0;
         const remaining = r.hasHolding ? (expired(r) ? held : desired) : 0;
         const multiplier = pointValue(r.optionTicker);
         const cost = (qty: number) => qty === 0 ? 0 : r.price === null || r.strike === null || multiplier === null ? null : round2(qty * r.price * multiplier);
         // A holding is valued at its close-out value; a buy transaction pays premium.
         r.dollarsExisting = cost(remaining);
         r.dollarsTransaction = cost(-transaction);
         r.dollars = r.hasHolding ? r.dollarsExisting : r.dollarsTransaction;
         // Coverage is separate from premium: buying adds it, selling removes it.
         r.protectedExisting = expired(r) || remaining === 0 ? 0 : r.strike === null || multiplier === null ? null : round2(r.strike * multiplier * remaining);
         r.protectedTransaction = transaction === 0 ? 0 : r.strike === null || multiplier === null ? null : round2(r.strike * multiplier * transaction);
      });
   });
   const sum = (key: keyof PhaOptionAmounts) => round2(rows.reduce((n, r) => n + (Number(r[key]) || 0), 0));
   const hasHoldings = rows.some(r => r.hasHolding);
   const hasTransactions = rows.some(r => r.hasTransaction);
   const dollarsExisting = sum('dollarsExisting');
   const dollarsTransaction = sum('dollarsTransaction');
   const protectedExisting = sum('protectedExisting');
   const protectedTransaction = sum('protectedTransaction');
   return {
      hasHoldings, hasTransactions, mixed: hasHoldings && hasTransactions,
      dollarsExisting, dollarsTransaction, protectedExisting, protectedTransaction,
      dollars: hasHoldings ? dollarsExisting : dollarsTransaction,
      dollarsProtected: hasHoldings ? protectedExisting : protectedTransaction,
      unpricedRows: rows.filter(r => r.strike !== null && (r.dollarsExisting === null || r.dollarsTransaction === null)).length
   };
}

/** Unit settlement payout, matching the Hedge Pad's expiration result before signed quantity. */
export function optionSettlementValue(type: PhaOptionType, strike: number, settlement: number | null | undefined): number | null {
   if (settlement === null || settlement === undefined || !isFinite(settlement) || strike === null || !isFinite(strike)) {
      return null;
   }
   return Math.max(0, type === 'Call' ? settlement - strike : strike - settlement);
}
